import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Users, Database, ChevronLeft, ChevronRight, Check, X, RefreshCw, Shield, Activity, Play, Brain, AlertTriangle, AlertCircle, Info, CheckCircle, Megaphone, UserX, UserCheck, Trash2, Clock, BookOpen, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Newspaper, ExternalLink, Search, SlidersHorizontal, Mail, Phone, CreditCard, Key, LogOut, ChevronRight as ChevronRightIcon, BarChart2, ArrowUpRight } from 'lucide-react';
import TradeReplay from '../components/charts/TradeReplay';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ADMIN_CACHE_TTLS_MS = {
  jobDashboard: 20_000,
  jobRuns: 10_000,
  actAnalytics: 120_000,
  foHealthSummary: 180_000,
  foHealthContracts: 180_000,
};

const adminPageCache = {
  jobDashboard: null,
  jobRuns: new Map(),
  actAnalytics: null,
  foHealthSummary: null,
  foHealthContracts: new Map(),
};

function getFreshAdminCache(entry, ttlMs) {
  if (!entry) return null;
  return (Date.now() - entry.at) < ttlMs ? entry.value : null;
}

function setAdminCache(slot, value) {
  adminPageCache[slot] = { value, at: Date.now() };
}

const ALL_PERMISSIONS = [
  { key: 'algo:use',        label: 'Algo Flows' },
  { key: 'nyx:trader',      label: 'Nyx AI Trader' },
  { key: 'decision:engine', label: 'Decision Engine' },
  { key: 'simulation:use',  label: 'Simulation' },
  { key: 'scanner:advanced',label: 'Advanced Scanners' },
  { key: 'report:pdf',      label: 'PDF Reports' },
  { key: 'report:email',    label: 'Email Reports' },
  { key: 'social:share',    label: 'Social Sharing (Insta/etc)' },
];

const PLAN_DEFAULT_PERMISSIONS = {
  free:    [],
  premium: ['algo:use', 'nyx:trader', 'decision:engine', 'simulation:use', 'scanner:advanced', 'report:pdf', 'report:email'],
  admin:   ALL_PERMISSIONS.map(p => p.key),  // admin gets everything — no restrictions
};

const PLAN_COLORS = {
  admin:   'bg-red-500/20 text-red-400',
  premium: 'bg-indigo-500/20 text-indigo-400',
  free:    'bg-gray-500/20 text-gray-400',
};

// ─── Users Tab ────────────────────────────────────────────────────────────────

function ExpiryBadge({ plan_expires_at, plan_expired }) {
  if (!plan_expires_at) return null;
  const exp = new Date(plan_expires_at);
  const daysLeft = Math.ceil((exp - Date.now()) / 86400000);
  if (plan_expired) {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#ef444420', color: '#ef4444' }}>
        EXPIRED
      </span>
    );
  }
  const color = daysLeft <= 3 ? '#f97316' : daysLeft <= 7 ? '#eab308' : '#22c55e';
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: `${color}20`, color }}>
      {daysLeft}d left
    </span>
  );
}

function UserDrawer({ user, colors, onClose, onRefresh }) {
  const [activeSection, setActiveSection] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [panelPlan, setPanelPlan] = useState(user.plan);
  const [panelPerms, setPanelPerms] = useState(user.permissions || []);
  const [panelExpiry, setPanelExpiry] = useState(() => {
    if (!user.plan_expires_at) return '';
    const d = new Date(user.plan_expires_at);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [panelName, setPanelName] = useState(user.full_name || '');
  const [panelEmail, setPanelEmail] = useState(user.email || '');
  const [panelPhone, setPanelPhone] = useState(user.phone || '');
  const [profileError, setProfileError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Trades + stats tab state
  const [trades, setTrades] = useState(null);
  const [stats, setStats] = useState(null);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (activeSection === 'trades' && trades === null && !tradesLoading) {
      setTradesLoading(true);
      axios.get(`${API_URL}/admin/users/${user.user_id}/trades?limit=50`)
        .then(r => setTrades(r.data))
        .catch(() => setTrades([]))
        .finally(() => setTradesLoading(false));
    }
    if ((activeSection === 'stats' || activeSection === 'behaviour') && stats === null && !statsLoading) {
      setStatsLoading(true);
      axios.get(`${API_URL}/admin/users/${user.user_id}/stats`)
        .then(r => setStats(r.data))
        .catch(() => setStats({}))
        .finally(() => setStatsLoading(false));
    }
  }, [activeSection, trades, stats, tradesLoading, statsLoading, user.user_id]);

  const flash = (msg) => { setSaveSuccess(msg); setTimeout(() => setSaveSuccess(''), 2000); };

  const saveProfile = async () => {
    setProfileError('');
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/admin/users/${user.user_id}/profile`, { full_name: panelName, email: panelEmail, phone: panelPhone });
      await onRefresh();
      flash('Profile saved');
    } catch (e) {
      setProfileError(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const expires_at = panelExpiry ? new Date(panelExpiry).toISOString() : null;
      await axios.put(`${API_URL}/admin/users/${user.user_id}/plan`, { plan: panelPlan, expires_at });
      await onRefresh();
      flash('Plan saved');
    } finally { setSaving(false); }
  };

  const savePerms = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/users/${user.user_id}/permissions`, { permissions: panelPerms });
      await onRefresh();
      flash('Permissions saved');
    } finally { setSaving(false); }
  };

  const togglePerm = (key) => {
    setPanelPerms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const disableUser = async () => {
    setActionLoading('disable');
    try {
      await axios.patch(`${API_URL}/admin/users/${user.user_id}/disable`);
      await onRefresh();
      flash('User disabled');
    } finally { setActionLoading(null); }
  };

  const enableUser = async () => {
    setActionLoading('enable');
    try {
      await axios.patch(`${API_URL}/admin/users/${user.user_id}/enable`);
      await onRefresh();
      flash('User enabled');
    } finally { setActionLoading(null); }
  };

  const forceLogoutUser = async () => {
    setActionLoading('force-logout');
    try {
      await axios.patch(`${API_URL}/admin/users/${user.user_id}/force-logout`);
      await onRefresh();
      flash('Force logged out');
    } finally { setActionLoading(null); }
  };

  const deleteUser = async () => {
    setActionLoading('delete');
    try {
      await axios.delete(`${API_URL}/admin/users/${user.user_id}`);
      await onRefresh();
      onClose();
    } finally { setActionLoading(null); }
  };

  const sections = [
    { key: 'profile',   label: 'Profile',   Icon: Mail },
    { key: 'plan',      label: 'Plan',       Icon: CreditCard },
    { key: 'perms',     label: 'Perms',      Icon: Key },
    { key: 'trades',    label: 'Trades',     Icon: BarChart2 },
    { key: 'stats',     label: 'Stats',      Icon: TrendingUp },
    { key: 'behaviour', label: 'Behaviour',  Icon: Activity },
    { key: 'actions',   label: 'Actions',    Icon: Shield },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rounded-t-2xl flex flex-col"
        style={{ backgroundColor: colors.cardBg, maxHeight: '90vh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: colors.border }} />
        </div>

        {/* User header */}
        <div className="px-4 pt-2 pb-3 border-b" style={{ borderColor: colors.border }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: `${colors.accent}25`, color: colors.accent }}>
                {(user.full_name || user.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: colors.primaryText }}>
                  {user.full_name || '—'}
                </p>
                <p className="text-xs truncate max-w-[200px]" style={{ color: colors.secondaryText }}>{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: user.is_active ? '#22c55e' : '#ef4444' }}>
                ● {user.is_active ? 'Active' : 'Disabled'}
              </span>
              <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: colors.secondaryText, backgroundColor: colors.inputBg }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Badges row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${PLAN_COLORS[user.plan] || PLAN_COLORS.free}`}>
              {user.plan}
            </span>
            {user.plan === 'premium' && <ExpiryBadge plan_expires_at={user.plan_expires_at} plan_expired={user.plan_expired} />}
            {user.subscription?.autopay_active && (
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#10b98120', color: '#10b981' }}>
                AUTO-PAY
              </span>
            )}
            {saveSuccess && (
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#22c55e20', color: '#22c55e' }}>
                ✓ {saveSuccess}
              </span>
            )}
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex border-b" style={{ borderColor: colors.border }}>
          {sections.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className="flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition"
              style={{
                color: activeSection === key ? colors.accent : colors.secondaryText,
                borderBottom: activeSection === key ? `2px solid ${colors.accent}` : '2px solid transparent',
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Section content */}
        <div className="overflow-y-auto flex-1 px-4 py-4 space-y-3">

          {activeSection === 'profile' && (
            <>
              <div>
                <label className="text-xs mb-1 block" style={{ color: colors.secondaryText }}>Full Name</label>
                <input value={panelName} onChange={e => setPanelName(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                  style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: colors.secondaryText }}>Email</label>
                <input value={panelEmail} onChange={e => setPanelEmail(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                  style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: colors.secondaryText }}>Phone</label>
                <input value={panelPhone} onChange={e => setPanelPhone(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm border outline-none"
                  style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }} />
              </div>
              {profileError && <p className="text-xs text-red-400">{profileError}</p>}
              <button onClick={saveProfile} disabled={saving}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-40"
                style={{ backgroundColor: colors.accent, color: '#fff' }}>
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
              <div className="mt-2 pt-3 border-t" style={{ borderColor: colors.border }}>
                <p className="text-xs mb-1" style={{ color: colors.secondaryText }}>User ID</p>
                <p className="text-xs font-mono break-all" style={{ color: colors.secondaryText }}>{user.user_id}</p>
                <p className="text-xs mt-1" style={{ color: colors.secondaryText }}>
                  Joined {formatDate(user.created_at)}
                </p>
              </div>
            </>
          )}

          {activeSection === 'plan' && (
            <>
              <p className="text-xs font-medium" style={{ color: colors.secondaryText }}>Select Plan</p>
              <div className="grid grid-cols-3 gap-2">
                {['free', 'premium', 'admin'].map(p => (
                  <button key={p}
                    onClick={() => { setPanelPlan(p); if (p !== 'premium') setPanelExpiry(''); }}
                    className={`py-2.5 rounded-lg text-xs font-semibold uppercase transition ${PLAN_COLORS[p]}`}
                    style={{
                      outline: panelPlan === p ? `2px solid ${colors.accent}` : 'none',
                      opacity: panelPlan === p ? 1 : 0.5,
                    }}>
                    {p}
                  </button>
                ))}
              </div>
              {panelPlan === 'premium' && (
                <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: colors.inputBg }}>
                  <label className="flex items-center gap-1.5 text-xs font-medium" style={{ color: colors.secondaryText }}>
                    <Clock className="w-3 h-3" /> Plan Expiry
                  </label>
                  <input type="datetime-local" value={panelExpiry} onChange={e => setPanelExpiry(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-xs border outline-none"
                    style={{ backgroundColor: colors.cardBg, color: colors.primaryText, borderColor: colors.border }} />
                  {panelExpiry && (
                    <button onClick={() => setPanelExpiry('')} className="text-xs" style={{ color: colors.secondaryText }}>
                      Clear (no expiry)
                    </button>
                  )}
                </div>
              )}
              {user.subscription && (
                <div className="rounded-lg p-3" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-xs font-medium mb-1.5" style={{ color: colors.secondaryText }}>Subscription</p>
                  <div className="grid grid-cols-2 gap-y-1.5 text-xs">
                    {[
                      ['Tier', user.subscription.tier],
                      ['Billing', user.subscription.billing],
                      ['Status', user.subscription.status],
                      ['Auto-Pay', user.subscription.autopay_active ? 'On' : 'Off'],
                    ].map(([k, v]) => (
                      <React.Fragment key={k}>
                        <span style={{ color: colors.secondaryText }}>{k}</span>
                        <span style={{ color: colors.primaryText }}>{v}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={savePlan} disabled={saving}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-40"
                style={{ backgroundColor: colors.accent, color: '#fff' }}>
                {saving ? 'Saving…' : 'Save Plan'}
              </button>
            </>
          )}

          {activeSection === 'perms' && (
            <>
              <p className="text-xs" style={{ color: colors.secondaryText }}>
                Greyed-out permissions are included by the plan and cannot be removed here.
              </p>
              <div className="space-y-2">
                {ALL_PERMISSIONS.map(({ key, label }) => {
                  const fromPlan = (PLAN_DEFAULT_PERMISSIONS[panelPlan] || []).includes(key);
                  const overrideOn = panelPerms.includes(key);
                  const hasAccess = fromPlan || overrideOn;
                  return (
                    <button
                      key={key}
                      onClick={() => !fromPlan && togglePerm(key)}
                      disabled={fromPlan}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition"
                      style={{
                        backgroundColor: hasAccess ? `${colors.accent}10` : colors.inputBg,
                        borderColor: hasAccess ? `${colors.accent}40` : colors.border,
                        opacity: fromPlan ? 0.6 : 1,
                        cursor: fromPlan ? 'default' : 'pointer',
                      }}
                    >
                      <span className="text-sm" style={{ color: hasAccess ? colors.primaryText : colors.secondaryText }}>{label}</span>
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: hasAccess ? colors.accent : colors.border }}>
                        {hasAccess && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <button onClick={savePerms} disabled={saving}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-40"
                style={{ backgroundColor: colors.accent, color: '#fff' }}>
                {saving ? 'Saving…' : 'Save Permissions'}
              </button>
            </>
          )}

          {activeSection === 'trades' && (
            <div>
              {tradesLoading && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} />
                </div>
              )}
              {!tradesLoading && trades !== null && trades.length === 0 && (
                <p className="text-center py-8 text-xs" style={{ color: colors.secondaryText }}>No trades yet</p>
              )}
              {!tradesLoading && trades && trades.length > 0 && (
                <div className="space-y-2">
                  {trades.map(t => {
                    const won = t.pnl > 0;
                    const lost = t.pnl < 0;
                    const pnlColor = won ? '#22c55e' : lost ? '#ef4444' : colors.secondaryText;
                    const entryDate = t.entry_date ? new Date(t.entry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
                    return (
                      <div key={t.trade_id}
                        className="rounded-xl border px-3 py-2.5 space-y-1"
                        style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold font-mono" style={{ color: colors.primaryText }}>{t.symbol}</span>
                          <span className="text-xs font-semibold" style={{ color: pnlColor }}>
                            {t.pnl != null ? `${t.pnl >= 0 ? '+' : ''}₹${Math.round(t.pnl).toLocaleString('en-IN')}` : '—'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px]" style={{ color: colors.secondaryText }}>
                            {entryDate} · {t.exit_reason || 'Open'}
                          </span>
                          <div className="flex items-center gap-2 text-[10px]" style={{ color: colors.secondaryText }}>
                            {t.actual_r != null && (
                              <span style={{ color: t.actual_r >= 1 ? '#22c55e' : t.actual_r < 0 ? '#ef4444' : colors.secondaryText }}>
                                {t.actual_r >= 0 ? '+' : ''}{t.actual_r.toFixed(1)}R
                              </span>
                            )}
                            {t.pnl_pct != null && (
                              <span style={{ color: pnlColor }}>
                                {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </div>
                        {t.discipline_snap && (
                          <div className="flex items-center gap-1 flex-wrap pt-0.5">
                            {Object.entries(t.discipline_snap).slice(0, 4).map(([k, v]) => (
                              <span key={k} className="px-1.5 py-0.5 rounded text-[9px] font-medium"
                                style={{ backgroundColor: v ? '#22c55e15' : '#ef444415', color: v ? '#22c55e' : '#ef4444' }}>
                                {k.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeSection === 'stats' && (
            <div className="space-y-3">
              {statsLoading && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} />
                </div>
              )}
              {!statsLoading && stats && (() => {
                const scoreColor = s => s >= 65 ? '#22c55e' : s >= 50 ? '#eab308' : '#ef4444';
                const inr = n => n != null ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—';
                const pnlStr = n => n != null ? `${n >= 0 ? '+' : ''}${inr(n)}` : '—';
                const cap = stats.capital || {};
                const risk = stats.risk || {};
                return (
                  <>
                    {/* Capital & Tier */}
                    <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Capital & Tier</p>
                        {cap.tier && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                            style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>{cap.tier}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {[
                          ['Behavior Score', cap.behavior_score != null ? `${cap.behavior_score}/100` : '—'],
                          ['Active Daily Cap', inr(cap.active_capital)],
                          ['Locked Capital', inr(cap.locked_capital)],
                          ['Total Capital', inr(cap.total_capital)],
                          ['Virtual Balance', inr(cap.virtual_balance)],
                          ['Projected Cap', inr(cap.projected_cap)],
                        ].map(([label, val]) => (
                          <React.Fragment key={label}>
                            <span style={{ color: colors.secondaryText }}>{label}</span>
                            <span className="font-medium" style={{ color: label === 'Behavior Score' && cap.behavior_score != null ? scoreColor(cap.behavior_score) : colors.primaryText }}>{val}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Risk Profile */}
                    <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Risk Profile</p>
                        <span className="text-[10px] font-medium" style={{ color: risk.questionnaire_done ? '#22c55e' : '#f59e0b' }}>
                          {risk.questionnaire_done ? '✓ Questionnaire done' : '⚠ Questionnaire pending'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {[
                          ['Profile Type', risk.profile_type || '—'],
                          ['Trade Style', risk.trade_style || '—'],
                          ['Risk % / Trade', risk.recommended_risk_pct != null ? `${risk.recommended_risk_pct}%` : '—'],
                          ['Risk Score', risk.risk_score != null ? risk.risk_score : '—'],
                        ].map(([label, val]) => (
                          <React.Fragment key={label}>
                            <span style={{ color: colors.secondaryText }}>{label}</span>
                            <span className="font-medium capitalize" style={{ color: colors.primaryText }}>{val}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Trade Summary */}
                    <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Trade Summary</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {[
                          ['Total Trades', stats.total_trades ?? '—'],
                          ['Win Rate', stats.win_rate != null ? `${stats.win_rate}%` : '—'],
                          ['Wins / Losses', `${stats.wins ?? 0} / ${stats.losses ?? 0}`],
                          ['Total P&L', pnlStr(stats.total_pnl)],
                          ['Avg P&L', pnlStr(stats.avg_pnl)],
                          ['Avg R', stats.avg_r != null ? `${stats.avg_r >= 0 ? '+' : ''}${stats.avg_r.toFixed(2)}R` : '—'],
                          ['Best Trade', pnlStr(stats.best_trade)],
                          ['Worst Trade', pnlStr(stats.worst_trade)],
                        ].map(([label, val]) => (
                          <React.Fragment key={label}>
                            <span style={{ color: colors.secondaryText }}>{label}</span>
                            <span className="font-medium" style={{ color: colors.primaryText }}>{val}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Discipline Score */}
                    {stats.discipline_score != null ? (
                      <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Discipline Score</p>
                          <span className="text-lg font-bold" style={{ color: scoreColor(stats.discipline_score) }}>
                            {stats.discipline_score}
                          </span>
                        </div>
                        {Object.keys(stats.component_scores).length > 0 && (
                          <div className="space-y-1.5">
                            {Object.entries(stats.component_scores).map(([k, v]) => (
                              <div key={k}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-[10px] capitalize" style={{ color: colors.secondaryText }}>{k.replace(/_/g, ' ')}</span>
                                  <span className="text-[10px] font-medium" style={{ color: scoreColor(v) }}>{v}</span>
                                </div>
                                <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
                                  <div className="h-1 rounded-full" style={{ width: `${v}%`, backgroundColor: scoreColor(v) }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-center py-2" style={{ color: colors.secondaryText }}>No discipline score yet</p>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {activeSection === 'behaviour' && (
            <div className="space-y-3">
              {statsLoading && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} />
                </div>
              )}
              {!statsLoading && stats && (() => {
                const act = stats.act || {};
                const beh = stats.behaviour || {};
                const inr = n => n != null ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—';
                return (
                  <>
                    {/* ACT / Trade Preparation */}
                    <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>ACT Preparation</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {[
                          ['Plans Scored', act.plans_scored ?? '—'],
                          ['Avg Score', act.avg_score != null ? `${act.avg_score}/100` : '—'],
                          ['Entered', act.enter_count ?? 0],
                          ['Waited', act.wait_count ?? 0],
                          ['Skipped', act.skip_count ?? 0],
                        ].map(([label, val]) => (
                          <React.Fragment key={label}>
                            <span style={{ color: colors.secondaryText }}>{label}</span>
                            <span className="font-medium" style={{ color: label === 'Avg Score' && act.avg_score != null ? (act.avg_score >= 65 ? '#22c55e' : act.avg_score >= 45 ? '#eab308' : '#ef4444') : colors.primaryText }}>{val}</span>
                          </React.Fragment>
                        ))}
                      </div>
                      {act.top_flags?.length > 0 && (
                        <div className="pt-2 border-t" style={{ borderColor: colors.border }}>
                          <p className="text-[10px] mb-1.5" style={{ color: colors.secondaryText }}>Top repeated flags</p>
                          <div className="flex flex-wrap gap-1.5">
                            {act.top_flags.map(({ flag, cnt }) => (
                              <span key={flag} className="px-2 py-0.5 rounded text-[10px] font-medium"
                                style={{ backgroundColor: '#ef444415', color: '#ef4444' }}>
                                {flag} ×{cnt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {(!act.plans_scored || act.plans_scored === 0) && (
                        <p className="text-[10px]" style={{ color: colors.secondaryText }}>No ACT plans submitted yet</p>
                      )}
                    </div>

                    {/* Losing Streak */}
                    <div className="rounded-xl border p-3" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Losing Streak</p>
                        <span className="text-lg font-bold"
                          style={{ color: beh.losing_streak >= 3 ? '#ef4444' : beh.losing_streak >= 2 ? '#f59e0b' : '#22c55e' }}>
                          {beh.losing_streak ?? 0} days
                        </span>
                      </div>
                      {beh.losing_streak >= 2 && (
                        <p className="text-[10px] mt-1" style={{ color: '#f59e0b' }}>
                          {beh.losing_streak >= 3
                            ? `${beh.losing_streak} consecutive losing sessions — consider reaching out`
                            : '2 losing sessions in a row — watch for revenge trading'}
                        </p>
                      )}
                    </div>

                    {/* Best / Worst Trading Hour */}
                    {(beh.best_hour || beh.worst_hour) && (
                      <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Time of Day</p>
                        <div className="grid grid-cols-2 gap-2">
                          {beh.best_hour && (
                            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#22c55e12', border: '1px solid #22c55e30' }}>
                              <p className="text-[10px]" style={{ color: '#22c55e' }}>Best Hour</p>
                              <p className="text-sm font-bold mt-0.5" style={{ color: '#22c55e' }}>{beh.best_hour.label}</p>
                              <p className="text-[10px]" style={{ color: '#22c55e' }}>Avg {inr(beh.best_hour.avg_pnl)}</p>
                            </div>
                          )}
                          {beh.worst_hour && (
                            <div className="rounded-lg p-2.5" style={{ backgroundColor: '#ef444412', border: '1px solid #ef444430' }}>
                              <p className="text-[10px]" style={{ color: '#ef4444' }}>Worst Hour</p>
                              <p className="text-sm font-bold mt-0.5" style={{ color: '#ef4444' }}>{beh.worst_hour.label}</p>
                              <p className="text-[10px]" style={{ color: '#ef4444' }}>Avg {inr(beh.worst_hour.avg_pnl)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Strategy Tags */}
                    {beh.top_tags?.length > 0 && (
                      <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Top Strategy Tags</p>
                        <div className="flex flex-wrap gap-1.5">
                          {beh.top_tags.map(({ tag, cnt }) => (
                            <span key={tag} className="px-2 py-0.5 rounded text-[10px] font-medium"
                              style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
                              {tag} ×{cnt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {activeSection === 'actions' && (
            <div className="space-y-3">
              {/* Enable / Disable */}
              {user.is_active ? (
                <button onClick={disableUser} disabled={!!actionLoading}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition disabled:opacity-40"
                  style={{ backgroundColor: '#ef444410', borderColor: '#ef444430', color: '#ef4444' }}>
                  <div className="flex items-center gap-3">
                    <UserX className="w-4 h-4" />
                    <div className="text-left">
                      <p className="text-sm font-semibold">Disable Account</p>
                      <p className="text-[10px] opacity-70">Block login, keep data</p>
                    </div>
                  </div>
                  {actionLoading === 'disable' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronRightIcon className="w-4 h-4 opacity-50" />}
                </button>
              ) : (
                <button onClick={enableUser} disabled={!!actionLoading}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition disabled:opacity-40"
                  style={{ backgroundColor: '#22c55e10', borderColor: '#22c55e30', color: '#22c55e' }}>
                  <div className="flex items-center gap-3">
                    <UserCheck className="w-4 h-4" />
                    <div className="text-left">
                      <p className="text-sm font-semibold">Enable Account</p>
                      <p className="text-[10px] opacity-70">Restore login access</p>
                    </div>
                  </div>
                  {actionLoading === 'enable' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronRightIcon className="w-4 h-4 opacity-50" />}
                </button>
              )}

              {/* Force Logout */}
              <button onClick={forceLogoutUser} disabled={!!actionLoading}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition disabled:opacity-40"
                style={{ backgroundColor: '#f59e0b10', borderColor: '#f59e0b30', color: '#f59e0b' }}>
                <div className="flex items-center gap-3">
                  <LogOut className="w-4 h-4" />
                  <div className="text-left">
                    <p className="text-sm font-semibold">Force Logout</p>
                    <p className="text-[10px] opacity-70">Invalidate all active sessions</p>
                  </div>
                </div>
                {actionLoading === 'force-logout' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronRightIcon className="w-4 h-4 opacity-50" />}
              </button>

              {/* Delete */}
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition"
                  style={{ backgroundColor: '#ef444410', borderColor: '#ef444430', color: '#ef4444' }}>
                  <div className="flex items-center gap-3">
                    <Trash2 className="w-4 h-4" />
                    <div className="text-left">
                      <p className="text-sm font-semibold">Delete User</p>
                      <p className="text-[10px] opacity-70">Permanently remove account</p>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 opacity-50" />
                </button>
              ) : (
                <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: '#ef444440', backgroundColor: '#ef444410' }}>
                  <p className="text-sm font-semibold text-center" style={{ color: '#ef4444' }}>
                    Delete {user.email}?
                  </p>
                  <p className="text-xs text-center" style={{ color: '#ef4444', opacity: 0.7 }}>This cannot be undone.</p>
                  <div className="flex gap-2">
                    <button onClick={deleteUser} disabled={actionLoading === 'delete'}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40"
                      style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                      {actionLoading === 'delete' ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                    <button onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-2.5 rounded-lg text-sm"
                      style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UsersTab({ colors }) {
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [autopayFilter, setAutopayFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const fetchUsers = useCallback(async () => {
    const res = await axios.get(`${API_URL}/admin/users`);
    setUsers(res.data);
    return res.data;
  }, []);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/audit-log`);
      setAuditLog(res.data);
    } catch (e) { setAuditLog([]); }
  }, []);

  useEffect(() => { fetchUsers(); fetchAudit(); }, [fetchUsers, fetchAudit]);

  const forceLogoutAll = async () => {
    setActionLoading('force-logout-all');
    try {
      const res = await axios.post(`${API_URL}/admin/users/force-logout-all`);
      setConfirmLogoutAll(false);
      alert(`Force logged out ${res.data.affected} users`);
    } finally { setActionLoading(null); }
  };

  const filteredUsers = users.filter(u => {
    if (statusFilter === 'active')   { if (!u.is_active || u.plan_expired) return false; }
    if (statusFilter === 'disabled') { if (u.is_active) return false; }
    if (statusFilter === 'expired')  { if (!u.plan_expired) return false; }
    if (planFilter !== 'all' && u.plan !== planFilter) return false;
    if (autopayFilter === 'on'  && !u.subscription?.autopay_active) return false;
    if (autopayFilter === 'off' && u.subscription?.autopay_active) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        u.email?.toLowerCase().includes(q) ||
        u.full_name?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        u.user_id?.toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) => {
    let av, bv;
    switch (sortBy) {
      case 'email':           av = a.email || '';            bv = b.email || '';            break;
      case 'plan':             av = a.plan || '';              bv = b.plan || '';              break;
      case 'plan_expires_at':  av = a.plan_expires_at || '';   bv = b.plan_expires_at || '';   break;
      default:                 av = a.created_at || '';        bv = b.created_at || '';
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const activeFilters = [statusFilter !== 'all', planFilter !== 'all', autopayFilter !== 'all'].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search users…"
            className="w-full rounded-xl pl-9 pr-3 py-2.5 text-sm border outline-none"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }}
          />
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className="relative flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition"
          style={{ backgroundColor: showFilters ? `${colors.accent}15` : colors.inputBg, borderColor: showFilters ? `${colors.accent}40` : colors.border, color: showFilters ? colors.accent : colors.secondaryText }}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {activeFilters > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center"
              style={{ backgroundColor: colors.accent, color: '#fff' }}>{activeFilters}</span>
          )}
        </button>
        <button
          onClick={() => fetchUsers()}
          className="p-2.5 rounded-xl border transition"
          style={{ backgroundColor: colors.inputBg, borderColor: colors.border, color: colors.secondaryText }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <p className="text-[10px] font-medium mb-1 uppercase tracking-wider" style={{ color: colors.secondaryText }}>Status</p>
              {['all', 'active', 'disabled', 'expired'].map(v => (
                <button key={v} onClick={() => setStatusFilter(v)}
                  className="w-full text-left px-2 py-1 rounded text-xs mb-0.5 transition"
                  style={{ backgroundColor: statusFilter === v ? `${colors.accent}20` : 'transparent', color: statusFilter === v ? colors.accent : colors.secondaryText, fontWeight: statusFilter === v ? 600 : 400 }}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                  <span className="ml-1 text-[10px] opacity-60">
                    ({v === 'all' ? users.length : v === 'active' ? users.filter(u => u.is_active && !u.plan_expired).length : v === 'disabled' ? users.filter(u => !u.is_active).length : users.filter(u => u.plan_expired).length})
                  </span>
                </button>
              ))}
            </div>
            <div>
              <p className="text-[10px] font-medium mb-1 uppercase tracking-wider" style={{ color: colors.secondaryText }}>Plan</p>
              {['all', 'free', 'premium', 'admin'].map(v => (
                <button key={v} onClick={() => setPlanFilter(v)}
                  className="w-full text-left px-2 py-1 rounded text-xs mb-0.5 transition"
                  style={{ backgroundColor: planFilter === v ? `${colors.accent}20` : 'transparent', color: planFilter === v ? colors.accent : colors.secondaryText, fontWeight: planFilter === v ? 600 : 400 }}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                  <span className="ml-1 text-[10px] opacity-60">
                    ({v === 'all' ? users.length : users.filter(u => u.plan === v).length})
                  </span>
                </button>
              ))}
            </div>
            <div>
              <p className="text-[10px] font-medium mb-1 uppercase tracking-wider" style={{ color: colors.secondaryText }}>Auto-Pay</p>
              {['all', 'on', 'off'].map(v => (
                <button key={v} onClick={() => setAutopayFilter(v)}
                  className="w-full text-left px-2 py-1 rounded text-xs mb-0.5 transition"
                  style={{ backgroundColor: autopayFilter === v ? `${colors.accent}20` : 'transparent', color: autopayFilter === v ? colors.accent : colors.secondaryText, fontWeight: autopayFilter === v ? 600 : 400 }}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                  <span className="ml-1 text-[10px] opacity-60">
                    ({v === 'all' ? users.length : v === 'on' ? users.filter(u => u.subscription?.autopay_active).length : users.filter(u => !u.subscription?.autopay_active).length})
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: colors.border }}>
            <span className="text-xs" style={{ color: colors.secondaryText }}>
              Showing <strong style={{ color: colors.primaryText }}>{filteredUsers.length}</strong> of {users.length} users
            </span>
            {activeFilters > 0 && (
              <button onClick={() => { setStatusFilter('all'); setPlanFilter('all'); setAutopayFilter('all'); }}
                className="text-xs" style={{ color: colors.accent }}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* View toggle + Force Logout All */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex rounded-xl p-0.5" style={{ backgroundColor: colors.inputBg }}>
          <button
            onClick={() => { setShowAudit(false); fetchUsers(); }}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
            style={{ backgroundColor: !showAudit ? colors.cardBg : 'transparent', color: !showAudit ? colors.primaryText : colors.secondaryText, boxShadow: !showAudit ? '0 1px 3px rgba(0,0,0,0.2)' : 'none' }}
          >
            Users
          </button>
          <button
            onClick={() => { setShowAudit(true); fetchAudit(); }}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
            style={{ backgroundColor: showAudit ? colors.cardBg : 'transparent', color: showAudit ? colors.primaryText : colors.secondaryText, boxShadow: showAudit ? '0 1px 3px rgba(0,0,0,0.2)' : 'none' }}
          >
            Audit Log
          </button>
        </div>
        {!confirmLogoutAll ? (
          <button onClick={() => setConfirmLogoutAll(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition"
            style={{ backgroundColor: '#f59e0b10', color: '#f59e0b', border: '1px solid #f59e0b30' }}>
            <UserX className="w-3 h-3" /> Force Logout All
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={forceLogoutAll} disabled={actionLoading === 'force-logout-all'}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold"
              style={{ backgroundColor: '#f59e0b', color: '#000' }}>
              {actionLoading === 'force-logout-all' ? '…' : 'Confirm'}
            </button>
            <button onClick={() => setConfirmLogoutAll(false)}
              className="px-3 py-1.5 rounded-xl text-xs"
              style={{ color: colors.secondaryText, backgroundColor: colors.inputBg }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {!showAudit ? (
        <div className="space-y-2">
          {filteredUsers.map(u => (
            <button
              key={u.user_id}
              onClick={() => setSelected(u)}
              className="w-full text-left rounded-xl border px-4 py-3 transition flex items-center gap-3"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: colors.border,
                opacity: u.is_active ? 1 : 0.6,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${colors.accent}40`}
              onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
            >
              {/* Avatar */}
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                {(u.full_name || u.email || '?')[0].toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate" style={{ color: colors.primaryText }}>
                    {u.full_name || u.email}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${PLAN_COLORS[u.plan] || PLAN_COLORS.free}`}>
                    {u.plan}
                  </span>
                  {u.plan === 'premium' && <ExpiryBadge plan_expires_at={u.plan_expires_at} plan_expired={u.plan_expired} />}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs truncate" style={{ color: colors.secondaryText }}>{u.email}</span>
                </div>
              </div>

              {/* Status + chevron */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-medium hidden sm:block" style={{ color: u.is_active ? '#22c55e' : '#ef4444' }}>
                  {u.is_active ? '● Active' : '● Off'}
                </span>
                <ChevronRightIcon className="w-4 h-4" style={{ color: colors.secondaryText }} />
              </div>
            </button>
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-center py-10 text-sm" style={{ color: colors.secondaryText }}>
              No users match this filter
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: colors.border }}>
          <table className="w-full min-w-[640px] text-sm">
            <thead style={{ backgroundColor: colors.inputBg }}>
              <tr>
                {['Admin', 'Target', 'Action', 'Old', 'New', 'When'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider"
                    style={{ color: colors.secondaryText }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {auditLog.map(a => (
                <tr key={a.id} className="border-t" style={{ borderColor: colors.border }}>
                  <td className="px-3 py-2 text-xs" style={{ color: colors.primaryText }}>{a.admin_email}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: colors.secondaryText }}>{a.target_email}</td>
                  <td className="px-3 py-2 text-xs font-mono" style={{ color: colors.accent }}>{a.action}</td>
                  <td className="px-3 py-2 text-xs max-w-24 truncate" style={{ color: colors.secondaryText }}>{a.old_value || '—'}</td>
                  <td className="px-3 py-2 text-xs max-w-24 truncate" style={{ color: colors.primaryText }}>{a.new_value || '—'}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: colors.secondaryText }}>{formatDateTime(a.created_at)}</td>
                </tr>
              ))}
              {auditLog.length === 0 && (
                <tr><td colSpan="6" className="px-3 py-6 text-center text-xs" style={{ color: colors.secondaryText }}>No audit entries yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* User detail drawer */}
      {selected && (
        <UserDrawer
          user={selected}
          colors={colors}
          onClose={() => setSelected(null)}
          onRefresh={async () => { const updated = await fetchUsers(); setSelected(prev => (updated || []).find(u => u.user_id === prev?.user_id) || prev); }}
        />
      )}
    </div>
  );
}

// ─── Database Tab ─────────────────────────────────────────────────────────────

function DatabaseTab({ colors }) {
  const [tables, setTables] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [schema, setSchema] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterCol, setFilterCol] = useState('');
  const [filterVal, setFilterVal] = useState('');
  const [editCell, setEditCell] = useState(null); // { rowIdx, col, value }
  const [saving, setSaving] = useState(false);

  // Query panel
  const [queryOpen, setQueryOpen] = useState(false);
  const [querySql, setQuerySql] = useState(
    "SELECT scanner_name, symbol, scan_date, signal_strength, timeframe, triggered_at\nFROM scanner_results\nORDER BY triggered_at DESC\nLIMIT 50"
  );
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState(null); // { columns, rows, count } | { error }

  const runQuery = async () => {
    if (!querySql.trim()) return;
    setQueryLoading(true);
    setQueryResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/db/query`, { sql: querySql });
      setQueryResult(res.data);
    } catch (e) {
      setQueryResult({ error: e?.response?.data?.detail || 'Query failed' });
    } finally {
      setQueryLoading(false);
    }
  };

  const LIMIT = 20;

  useEffect(() => {
    axios.get(`${API_URL}/admin/db/tables`).then(r => {
      setTables(r.data.tables);
      if (r.data.tables.length) selectTable(r.data.tables[0]);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTable = (t) => {
    setActiveTable(t);
    setPage(1);
    setFilterCol('');
    setFilterVal('');
    setEditCell(null);
  };

  const fetchRows = useCallback(async () => {
    if (!activeTable) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filterCol && filterVal) { params.set('filter_col', filterCol); params.set('filter_val', filterVal); }
      const res = await axios.get(`${API_URL}/admin/db/tables/${activeTable}?${params}`);
      setSchema(res.data.columns);
      setRows(res.data.rows);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } finally { setLoading(false); }
  }, [activeTable, page, filterCol, filterVal]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Detect primary key column (prefer 'id', else first column)
  const pkCol = schema.find(c => c.column_name === 'id')?.column_name || schema[0]?.column_name;

  const startEdit = (rowIdx, col, value) => {
    setEditCell({ rowIdx, col, value: value === null ? '' : String(value) });
  };

  const saveCell = async () => {
    if (!editCell || !pkCol) return;
    const row = rows[editCell.rowIdx];
    const pkVal = row[pkCol];
    if (pkVal === undefined || pkVal === null) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/admin/db/tables/${activeTable}/${pkCol}/${encodeURIComponent(pkVal)}`,
        { column: editCell.col, value: editCell.value }
      );
      // Update local row
      setRows(prev => prev.map((r, i) =>
        i === editCell.rowIdx ? { ...r, [editCell.col]: editCell.value } : r
      ));
      setEditCell(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const cancelEdit = () => setEditCell(null);

  const colDisplay = (col) => col.length > 14 ? col.slice(0, 13) + '…' : col;

  const cellDisplay = (val) => {
    if (val === null || val === undefined) return <span style={{ color: colors.secondaryText }} className="italic text-xs">null</span>;
    const s = String(val);
    return s.length > 40 ? s.slice(0, 39) + '…' : s;
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 h-full min-h-0">
      {/* Table sidebar — vertical scroll on desktop, horizontal scroll on mobile */}
      <div className="app-card sm:w-44 w-full shrink-0 rounded-xl border sm:overflow-y-auto overflow-x-auto" style={{ borderColor: colors.border, backgroundColor: colors.cardBg }}>
        <div className="sm:hidden flex gap-1 p-1">
          {tables.map(t => (
            <button key={t} onClick={() => selectTable(t)}
              className="whitespace-nowrap px-2.5 py-1.5 rounded-lg text-xs transition flex-shrink-0"
              style={{
                color: activeTable === t ? colors.accent : colors.primaryText,
                backgroundColor: activeTable === t ? `${colors.accent}15` : colors.inputBg,
                fontWeight: activeTable === t ? 600 : 400,
              }}>
              {t}
            </button>
          ))}
        </div>
        <div className="hidden sm:block">
        <div className="px-3 py-2 border-b text-xs font-semibold uppercase tracking-wider" style={{ borderColor: colors.border, color: colors.secondaryText }}>
          Tables
        </div>
        {tables.map(t => (
          <button
            key={t}
            onClick={() => selectTable(t)}
            className="w-full text-left px-3 py-2 text-xs transition border-b last:border-0"
            style={{
              borderColor: colors.border,
              color: activeTable === t ? colors.accent : colors.primaryText,
              backgroundColor: activeTable === t ? `${colors.accent}15` : 'transparent',
              fontWeight: activeTable === t ? 600 : 400,
            }}
          >
            {t}
          </button>
        ))}
        </div>
      </div>

      {/* Table viewer */}
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        {/* SQL Query Panel */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: colors.border }}>
          <button
            onClick={() => setQueryOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold"
            style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}
          >
            <span>SQL Query</span>
            <span style={{ fontSize: 10 }}>{queryOpen ? '▲' : '▼'}</span>
          </button>
          {queryOpen && (
            <div className="p-3 flex flex-col gap-2" style={{ backgroundColor: colors.cardBg }}>
              <textarea
                value={querySql}
                onChange={e => setQuerySql(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) runQuery(); }}
                placeholder="SELECT * FROM scanner_results WHERE scan_date = '2026-03-19' LIMIT 20"
                rows={3}
                className="w-full rounded border text-xs px-2 py-1.5 font-mono resize-none"
                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={runQuery}
                  disabled={queryLoading || !querySql.trim()}
                  className="px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40 transition"
                  style={{ backgroundColor: colors.accent, color: '#fff' }}
                >
                  {queryLoading ? 'Running…' : 'Run  (Ctrl+Enter)'}
                </button>
                {queryResult && !queryResult.error && (
                  <span className="text-xs" style={{ color: colors.secondaryText }}>{queryResult.count} row{queryResult.count !== 1 ? 's' : ''}</span>
                )}
                {queryResult?.error && (
                  <span className="text-xs text-red-400">{queryResult.error}</span>
                )}
              </div>
              {queryResult && !queryResult.error && queryResult.columns.length > 0 && (
                <div className="overflow-auto max-h-48 rounded border" style={{ borderColor: colors.border }}>
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0" style={{ backgroundColor: colors.inputBg }}>
                      <tr>
                        {queryResult.columns.map(c => (
                          <th key={c} className="px-2 py-1 text-left font-semibold border-b whitespace-nowrap"
                              style={{ borderColor: colors.border, color: colors.secondaryText }}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queryResult.rows.map((row, i) => (
                        <tr key={i} className="border-b" style={{ borderColor: colors.border }}>
                          {queryResult.columns.map(c => (
                            <td key={c} className="px-2 py-1 whitespace-nowrap max-w-xs truncate"
                                style={{ color: colors.primaryText }}
                                title={String(row[c] ?? '')}>
                              {row[c] === null ? <span className="italic opacity-40">null</span> : String(row[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterCol}
            onChange={e => { setFilterCol(e.target.value); setFilterVal(''); setPage(1); }}
            className="rounded px-2 py-1.5 text-xs border"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }}
          >
            <option value="">Filter column…</option>
            {schema.map(c => <option key={c.column_name} value={c.column_name}>{c.column_name}</option>)}
          </select>
          {filterCol && (
            <input
              value={filterVal}
              onChange={e => { setFilterVal(e.target.value); setPage(1); }}
              placeholder="Filter value…"
              className="rounded px-2 py-1.5 text-xs border flex-1"
              style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.border }}
            />
          )}
          <button onClick={fetchRows} className="p-1.5 rounded" style={{ color: colors.secondaryText }}>
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs sm:ml-auto" style={{ color: colors.secondaryText }}>
            {total} rows · page {page}/{pages}
          </span>
        </div>

        {/* Data grid */}
        <div className="flex-1 rounded-xl border overflow-auto" style={{ borderColor: colors.border }}>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-5 h-5 animate-spin" style={{ color: colors.secondaryText }} />
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: colors.inputBg }}>
                <tr>
                  {schema.map(c => (
                    <th key={c.column_name} className="px-3 py-2 text-left font-semibold border-b whitespace-nowrap"
                        style={{ borderColor: colors.border, color: colors.secondaryText }}>
                      {colDisplay(c.column_name)}
                      <span className="ml-1 font-normal text-opacity-50" style={{ color: colors.secondaryText, fontSize: 9 }}>
                        {c.data_type.replace('character varying', 'varchar').replace('timestamp with time zone', 'timestamptz').slice(0, 12)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="border-b hover:bg-white/5 transition-colors" style={{ borderColor: colors.border }}>
                    {schema.map(c => {
                      const isEditing = editCell?.rowIdx === ri && editCell?.col === c.column_name;
                      const isReadOnly = c.column_name === 'id' || c.column_name === 'token_version' || c.data_type.includes('uuid') && c.column_name === 'id';
                      return (
                        <td
                          key={c.column_name}
                          className="px-3 py-1.5 align-middle max-w-xs"
                          style={{ color: colors.primaryText }}
                          onDoubleClick={() => !isReadOnly && startEdit(ri, c.column_name, row[c.column_name])}
                        >
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <input
                                autoFocus
                                value={editCell.value}
                                onChange={e => setEditCell(prev => ({ ...prev, value: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveCell(); if (e.key === 'Escape') cancelEdit(); }}
                                className="rounded px-1.5 py-0.5 text-xs border w-full min-w-0"
                                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderColor: colors.accent }}
                              />
                              <button onClick={saveCell} disabled={saving}
                                className="shrink-0 p-0.5 rounded hover:text-green-400 transition" style={{ color: colors.secondaryText }}>
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={cancelEdit}
                                className="shrink-0 p-0.5 rounded hover:text-red-400 transition" style={{ color: colors.secondaryText }}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <span className={isReadOnly ? 'opacity-50' : 'cursor-text'} title={isReadOnly ? '' : 'Double-click to edit'}>
                              {cellDisplay(row[c.column_name])}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={schema.length} className="px-3 py-8 text-center" style={{ color: colors.secondaryText }}>
                      No rows found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1.5 rounded border disabled:opacity-30 transition"
            style={{ borderColor: colors.border, color: colors.primaryText }}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs" style={{ color: colors.secondaryText }}>
            Page {page} of {pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="p-1.5 rounded border disabled:opacity-30 transition"
            style={{ borderColor: colors.border, color: colors.primaryText }}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Job Dashboard Tab ────────────────────────────────────────────────────────

const JOB_META = {
  // Ingestion
  refresh_instruments:       { schedule: 'Monday 6:05 AM (weekly)', file: 'upstox_instruments.py',   source: 'Upstox instruments API (CDN .gz file)', destination: 'upstox_instrument_map.json (disk)',                summary: 'Downloads the full Upstox NSE symbol → instrument key mapping from Upstox CDN. Saves to upstox_instrument_map.json — required by all other Upstox API scripts to resolve trading identifiers.' },
  data_validation:           { schedule: 'Daily 5:30 AM',          file: 'validate_data.py',         source: 'market_data, market_features, market_data_live, market_sentiment, global_indices, analysis_predictions', destination: 'bug_reports (source=system, job_id=data_validation)', summary: 'Four-layer data quality audit: (1) Freshness watchdog — flags tables stale beyond threshold (market_data 3d, global_indices 5d, sentiment 5d, predictions 3d). (2) Cross-table price consistency — compares market_features.current_price vs market_data.close_price per symbol (>2% delta = bug). (3) Indicator NULL coverage — flags any indicator column (RSI, ADX, MACD, BB, volume_ratio) with >20% NULL across active symbols. (4) Coverage completeness — checks market_sentiment has ≥15 segments, global_indices has 10 symbols, analysis_predictions has 51 symbols on latest date. Also runs EOD OHLC sanity (null/zero/high<low/35% daily jump) and 30-day intraday candle gap checks.' },
  market_data_ingestion:     { schedule: 'Daily 4:00 PM',          file: 'upstox_historical.py',     source: 'Upstox EOD REST API (day interval)',   destination: 'market_data (daily OHLCV), market_features (24 indicators)', summary: 'Fetches today\'s EOD OHLCV candle from Upstox for all 51 stocks and also computes 24 technical indicators (RSI, MACD, SMA, etc.) — inserts ~51 rows into market_data and upserts market_features in a single run.' },
  news_sentiment_ingestion:  { schedule: 'Daily 8:00 AM',          file: 'news_sentiment_job.py',   source: 'NewsAPI.org (6 category queries) + RSS fallback',       destination: 'market_news (raw articles), market_sentiment (overall + 16 sectors, last 30 days)', summary: 'Fetches pre-market news headlines via NewsAPI for the sim_date window (prev-day 4 PM IST → 9:30 AM IST), scores each article with VADER sentiment, then aggregates per-day × segment rows into market_sentiment. Powers the Global Cues news feed and sector sentiment in Analysis Mode.' },
  global_indices_snapshot:   { schedule: 'Daily 8:45 AM',          file: 'global_indices_job.py',   source: 'yfinance (10 global indices)',                          destination: 'global_indices (daily snapshot), backfills last 30 days', summary: 'Fetches pre-market snapshots of 10 global indices (Dow, S&P 500, NASDAQ, Nikkei, Hang Seng, FTSE, DAX, USD Index, Crude Oil, Gold) via yfinance. US/Europe use previous close; Asia/Macro use opening price. Backfills last 30 days on first run. Powers the Global Cues indices panel.' },
  backfill_historical:       { schedule: 'Daily 5:30 PM',          file: 'backfill_historical_data.py', source: 'market_data_live (last 90 days)',    destination: 'market_features, scanner_results (last 90 days)', summary: 'Ensures market_features and scanner_results exist for all trading dates in the last 90 days. Reads 1-min candle data, computes indicators and 27 scanner signals — fills any gaps left by the primary pipelines.' },
  // Scanners
  scanner_execution:         { schedule: 'Every 30 min 9–15h',     file: 'run_scanners.py',          source: 'market_features (latest), market_data (last 252 rows), Redis market_sim:date', destination: 'scanner_results (27 signals)', summary: 'Evaluates 27 technical conditions per symbol (RSI, MACD, Bollinger Bands, volume spike, etc.) on daily EOD candles every 30 min during market hours. Writes binary pass/fail per scanner into scanner_results.' },
  intraday_scanner_5min:     { schedule: 'Every 5 min 9–15h',      file: 'run_scanners.py',          source: 'market_data_live aggregated to 5-min', destination: 'scanner_results',                                 summary: 'Same 27 scanner signals computed on 5-minute candles aggregated from market_data_live. Updates scanner_results every 5 min — powers the intraday scanner tab.' },
  intraday_scanner_15min:    { schedule: 'Every 15 min 9–15h',     file: 'run_scanners.py',          source: 'market_data_live aggregated to 15-min', destination: 'scanner_results',                                summary: 'Same 27 signals on 15-minute candles. Runs every 15 minutes during market hours — provides medium-term intraday signal confirmation alongside the 5-min scanner.' },
  swing_pattern_scan:        { schedule: 'Daily 4:30 PM',          file: 'scan_patterns.py',         source: 'market_data (daily EOD candles)',      destination: 'JSON files per symbol + date (local cache)',       summary: 'Detects 15 candlestick and chart patterns (engulfing, morning star, hammer, double top/bottom, flags) on daily EOD candles after market close. Saves pattern JSON files per stock per date.' },
  intraday_pattern_scan:     { schedule: 'Every 30 min 9–15h',     file: 'scan_patterns.py',         source: 'market_data_live (aggregated to 5-min and 15-min)', destination: 'JSON files per symbol + timeframe + date', summary: 'Same 15 pattern detections on 5-min and 15-min intraday candles. Runs every 30 min during market hours — powers intraday pattern alerts in the scanner.' },
  trade_intelligence:        { schedule: 'Every 10 min 9–15h',     file: 'trade_intelligence.py',    source: 'market_data, market_features, market_data_live', destination: 'Redis trade_intelligence:swing:{symbol} + trade_intelligence:intraday:{symbol}', summary: 'Computes swing + intraday trade intelligence scores (win rate, avg R:R, signal quality) for all active stocks every 10 min. Caches to Redis for fast display in the AI Coach and Trade page.' },
  // Trading
  activate_queued_orders:    { schedule: '9:15 AM (once)',          file: 'activate_queued_orders.py', source: 'orders table (status=QUEUED)',        destination: 'orders table (status → OPEN)',                     summary: 'Promotes all QUEUED orders to OPEN status at 9:15 AM market open. Orders placed pre-market sit in QUEUED state — this job makes them active and eligible for fills.' },
  evaluate_orders:           { schedule: 'Every min 9–15h',         file: 'evaluate_orders.py',       source: 'orders (OPEN LIMIT), Redis sim:price:{symbol}', destination: 'orders (→ EXECUTED), trades (new row on fill)', summary: 'Evaluates all OPEN LIMIT orders against current sim price every minute. BUY fills if candle_low ≤ limit; SELL fills if candle_high ≥ limit. Creates a trade row on execution.' },
  evaluate_positions:        { schedule: 'Every min 9–15h',         file: 'evaluate_positions.py',    source: 'trades (OPEN), market_data_live (live candles)', destination: 'trades (→ CLOSED with exit_reason + P&L)',       summary: 'Monitors all open positions every minute for SL or target triggers. SL fires if candle_low ≤ stop_loss; target fires if candle_high ≥ target. SL takes priority if both trigger on the same candle.' },
  evaluate_strategies:       { schedule: 'Every min 9–15h',         file: 'evaluate_strategies.py',   source: 'strategies (ACTIVE), market_features, Redis sim:price:{symbol} + cooldown keys', destination: 'orders (LIMIT entries), strategy_execution_logs, Redis decision cache', summary: 'Runs all active Algo Flow strategies against their stocks every minute. Checks daily trade limit, Redis cooldown, entry conditions (ALL/ANY), and trend filter — creates a LIMIT order if all pass.' },
  intraday_force_exit:       { schedule: '3:10 PM (once)',          file: 'evaluate_positions.py',    source: 'trades (INTRADAY + OPEN)',            destination: 'trades (→ CLOSED, exit_reason=EOD)',               summary: 'Force-closes all remaining INTRADAY open positions at 3:10 PM at the current sim price. Prevents intraday trades from accidentally carrying over to the next trading session.' },
  // Analysis
  outcome_tracker:           { schedule: 'Daily 4:45 PM',          file: 'worker.py (inline)',        source: 'analysis_predictions (yesterday), market_data (next 3 days prices)', destination: 'analysis_outcomes (return_1d, return_3d, was_winner)', summary: 'Pulls yesterday\'s Analysis Mode stock predictions, fetches actual closing prices for days 1–3, and records whether each prediction was correct. These outcomes feed the weekly weight recalibrator.' },
  weight_recalibrator:       { schedule: 'Monday 6:00 AM (weekly)', file: 'worker.py (inline)',        source: 'analysis_outcomes (last 30 days)',    destination: 'Redis analysis:weights, analysis_weight_history',  summary: 'Recalibrates composite score weights (technical, fundamental, sentiment, volume, confidence) by measuring win rate per dimension among top-quartile predictions. Normalises weights to [0.05–0.60] and saves to Redis.' },
  discipline_analysis:       { schedule: 'Sunday 6:00 PM (weekly)', file: 'worker.py (inline)',        source: 'strategies (ACTIVE, 7+ days, negative PnL), strategy_discipline_events', destination: 'strategy_discipline_events (HELD_THROUGH_LOSS badge +50 pts)', summary: 'Awards the HELD_THROUGH_LOSS discipline badge (+50 points) to strategies that endured 7+ days of negative PnL without a forced exit. Triggers weekly discipline score recalculation for all users.' },
  // Simulation
  market_sim_auto_start:     { schedule: '9:15 AM Mon–Fri',         file: 'market_sim_runner.py',     source: 'sim_config table (sim_date, speed)',  destination: 'Redis market_sim:state=RUNNING, sim_session table', summary: 'Auto-starts the sim tick engine at 9:15 AM on weekdays if sim_config.is_active=true. Begins replaying 35-day-delayed 1-min candles, publishing sim prices to Redis every 2 seconds.' },
  market_sim_auto_stop:      { schedule: '3:30 PM Mon–Fri',         file: 'market_sim_runner.py',     source: 'Redis market_sim:state',             destination: 'Redis market_sim:state=STOPPED, sim_session (end_time)', summary: 'Stops the sim tick engine at 3:30 PM, logs end_time to sim_session, and clears the in-memory runner. Ends the simulated trading session for the day.' },
  market_sim_advance_date:   { schedule: 'Daily midnight + 9:00 AM safety re-run', file: 'worker.py (inline)', source: 'Wall clock (today − 35 market days)', destination: 'sim_config.sim_date, Redis market_sim:date',     summary: 'Sets sim_date = today minus 35 market days at midnight. This is the single source of truth for all simulation date logic — every script and API reads from Redis market_sim:date.' },
  cancel_open_orders:        { schedule: '3:35 PM (once)',          file: 'cancel_open_orders.py',    source: 'orders (status OPEN or QUEUED, matching sim_date)', destination: 'orders (status → CANCELLED)',              summary: 'Cancels all unfilled OPEN and QUEUED orders matching today\'s sim_date at EOD. Falls back to real date if sim_date is unavailable. Keeps the order book clean for the next trading day.' },
  capital_daily_reset:       { schedule: '9:00 AM daily',           file: 'worker.py (inline)',        source: 'users table (behavior_score)',        destination: 'user_capital table (active_capital reset)',         summary: 'Resets each user\'s active trading capital at market open based on their discipline/behavior score from the prior session. Lower discipline score = reduced capital allocation for the day.' },
  prewarm_edge_scores:       { schedule: 'Daily 5:00 PM',           file: 'worker.py (inline)',        source: 'market_data, market_features',        destination: 'Redis decision_engine cache, decision_engine_cache table', summary: 'Pre-computes Edge Report (BUY/WAIT/AVOID) conviction scores for all 51 stocks after EOD ingestion. Warms the Redis cache so the AI Coach edge scores load instantly without DB queries.' },
  plan_expiry_downgrade:     { schedule: 'Daily 1:00 AM (nightly)', file: 'worker.py (inline)',        source: 'users.plan_expires_at',              destination: 'users.plan (→ user), users.token_version (incremented)', summary: 'Downgrades users whose paid plan has expired to the free tier and increments token_version to invalidate their JWT. Runs nightly — no manual action needed for subscription lapses.' },
  // Backfill
  backfill_daily_ohlcv:      { schedule: 'Daily 4:30 PM',           file: 'backfill_daily_ohlcv.py',   source: 'yfinance (Yahoo Finance)',             destination: 'market_data (missing daily rows only)',            summary: 'Fills only missing daily OHLCV rows for the last 30 days using yfinance. Does not overwrite existing rows — gap-fill only. Runs after market_data_ingestion to catch any failed or missing dates.' },
  backfill_1min_candles:     { schedule: 'Daily 4:20 AM',           file: 'backfill_1min_candles.py',  source: 'Upstox Historical Candles API (1-min)', destination: 'market_data_live (missing 1-min rows, last 60 days)', summary: 'Fills missing 1-minute OHLCV candles for the last 60 days using Upstox REST API. Checks expected candle count per session (375) and only inserts gaps — does not overwrite existing candles.' },
  backfill_fundamentals:     { schedule: 'Monday 6:00 AM (weekly)', file: 'fundamentals_job.py',       source: 'yfinance (Yahoo Finance)',             destination: 'stock_fundamentals, stock_events, stocks',         summary: 'Backfills PE ratio, EPS, market cap, dividends, splits and earnings dates for all 51 stocks via yfinance. Same as Fundamentals Ingestion — listed here as the scheduled backfill entry point.' },
  backfill_15yr_ohlcv:       { schedule: 'Manual trigger only',     file: 'backfill_historical.py',    source: 'yfinance (Yahoo Finance, 2010–present)', destination: 'market_data (full history per symbol)',            summary: 'On-demand job that fetches up to 15 years of daily OHLCV for all 51 stocks via yfinance. Takes 20–30 min to complete. Run once on a new VM or after a full DB reset — never auto-triggered.' },
  // F&O
  fo_contract_refresh:       { schedule: 'Monday 6:10 AM (weekly)', file: 'fo_contract_refresh.py',    source: 'Upstox NFO instruments list (CDN .gz)',  destination: 'fo_contracts (upsert NIFTY/BANKNIFTY active contracts)', summary: 'Downloads the Upstox NFO instruments file and upserts active NIFTY50/BANKNIFTY futures + ±10 strike options for the 2 nearest expiries into fo_contracts. Marks stale contracts inactive. Run after an expiry rollover or on a fresh VM.' },
  fo_backfill_1min:          { schedule: 'Daily 4:25 AM',           file: 'fo_backfill_1min.py',       source: 'Upstox Historical Candles API (1-min)',  destination: 'market_data_live (missing 1-min rows for F&O, last 60d)', summary: 'Fills missing 1-minute OHLCV candles for all active F&O contracts over the last 60 days. Uses the same market_data_live table as equities. Skips days after contract expiry. Gap-fill only — does not overwrite existing candles.' },
  fo_expiry_settlement:      { schedule: 'Daily 3:35 PM',           file: 'fo_expiry_settlement.py',   source: 'trades, fo_contracts, market_data_live', destination: 'trades (exit settled, status=CLOSED)',               summary: 'Settles all open F&O positions whose contract expires on sim_date. FUT → last 1-min close. CE → max(0, spot − strike). PE → max(0, strike − spot). OTM options expire worthless. Run manually only if the scheduler missed the 3:35 PM trigger.' },
  // Monitoring
  data_validation_intraday:  { schedule: 'Every min 9–15h',           file: 'validate_data.py --intraday', source: 'market_data_live (1-min candles, last 30 days)', destination: 'bug_reports (source=system, job_id=intraday_data_validation)', summary: 'Intraday-only candle check: verifies each active symbol has ≥350 of the expected 375 1-min candles per trading day, no intraday time gaps >5 min, no invalid OHLC values, and no minute-to-minute price jumps >12%. Flags incomplete or corrupted intraday sessions and files a bug_report.' },
  intraday_data_validation:  { schedule: 'Every min 9–15h',           file: 'validate_data.py --intraday', source: 'market_data_live (1-min candles, last 30 days)', destination: 'bug_reports (source=system, job_id=intraday_data_validation)', summary: 'Intraday-only candle check: verifies each active symbol has ≥350 of the expected 375 1-min candles per trading day, no intraday time gaps >5 min, no invalid OHLC values, and no minute-to-minute price jumps >12%. Flags incomplete or corrupted intraday sessions and files a bug_report.' },
  error_log_scanner:          { schedule: 'Every hour (:30)',           file: 'error_log_scanner.py',       source: 'OpenObserve (ERROR-level events), job_runs (failed)', destination: 'bug_reports (source=system)', summary: 'Scans the last hour of OpenObserve logs for ERROR-level events and checks job_runs for failures. Deduplicates against existing OPEN system bugs (6-hour window) to suppress noise. Creates a bug_report row for each new actionable error. Known-noisy patterns (404s, "no candles", Redis blips) are filtered out.' },
  // Learning
  act_weekly_maintenance:    { schedule: 'Sunday 6:00 PM (weekly)', file: 'learning_act.py',           source: 'market_data, market_features',        destination: 'learning_scenarios (new + repaired), act_daily_sets (60-day pre-alloc), market_features (pruned >365d)', summary: 'Weekly ACT system maintenance: backfills market_features for last 14 days, repairs NULL indicators in learning_scenarios, generates new swing + intraday scenarios, pre-allocates sets for next 60 days, and prunes old data.' },
  act_bootstrap:             { schedule: 'Manual / on-demand only', file: 'learning_act.py',           source: 'market_data (last 300 days)',         destination: 'learning_scenarios (initial seed pool)',            summary: 'One-time bootstrap that seeds the ACT learning scenario pool using 300 days of historical market data. Only needed on first VM setup or after a full data reset — do not run routinely.' },
  act_daily_allocate:        { schedule: 'Daily 6:30 PM',           file: 'learning_act.py',           source: 'learning_scenarios table',           destination: 'act_daily_sets (5 scenarios per user per trade type)', summary: 'Pre-allocates 5 ACT practice scenarios per trade type per user for each of the next 60 days. Excludes scenarios used in the last 60 days to prevent repetition.' },
  act_score_scenarios:       { schedule: 'Daily 7:00 PM',           file: 'learning_act.py',           source: 'learning_scenarios (unscored, quality_score IS NULL)', destination: 'learning_scenarios (quality_score, clarity_score, educational_value_score)', summary: 'Scores all unscored ACT scenarios using the quality scorer service — runs after act_daily_allocate so newly created scenarios are scored the same evening before being allocated.' },
};

const CATEGORY_ORDER = ['Ingestion', 'Scanners', 'Trading', 'Analysis', 'Simulation', 'Backfill', 'F&O', 'Learning', 'Monitoring'];
const CATEGORY_COLORS = {
  Ingestion:  { dot: '#6366F1', bg: '#6366F110' },
  Scanners:   { dot: '#8b5cf6', bg: '#8b5cf610' },
  Trading:    { dot: '#10b981', bg: '#10b98110' },
  Analysis:   { dot: '#f59e0b', bg: '#f59e0b10' },
  Simulation: { dot: '#64748b', bg: '#64748b10' },
  Backfill:   { dot: '#f97316', bg: '#f9731610' },
  'F&O':      { dot: '#ec4899', bg: '#ec489910' },
  Learning:   { dot: '#06b6d4', bg: '#06b6d410' },
  Monitoring: { dot: '#ef4444', bg: '#ef444410' },
};
const STATUS_STYLE = {
  success: { bg: '#16a34a', label: '✓' },
  failed:  { bg: '#ef4444', label: '✗' },
  partial: { bg: '#f59e0b', label: '!' },
  missed:  { bg: '#f97316', label: '?' },
  no_data: { bg: '#1e293b', label: '—' },
};
const CURRENT_STATUS_STYLE = {
  success: { bg: '#16a34a', label: 'Completed' },
  failed:  { bg: '#ef4444', label: 'Failed' },
  running: { bg: '#f59e0b', label: 'In Progress' },
  queued:  { bg: '#64748b', label: 'Queued' },
  cancelled: { bg: '#94a3b8', label: 'Cancelled' },
  stale:   { bg: '#f97316', label: 'Stale' },
  timed_out: { bg: '#dc2626', label: 'Timed Out' },
  no_data: { bg: '#1e293b', label: '—' },
};

const isActiveRunStatus = (status) => status === 'running' || status === 'queued';

const formatJobDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatJobAge = (value) => {
  if (!value) return '—';
  const diff = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
};

function JobDashboardTab({ colors }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tooltip, setTooltip]   = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [runningJob, setRunningJob]   = useState(null);
  const [runningAll, setRunningAll]   = useState(false);
  const [clearingStuck, setClearingStuck] = useState(false);
  const [cancellingAll, setCancellingAll] = useState(false);
  const [cancellingJobs, setCancellingJobs] = useState({});
  const [, setTick] = useState(0);
  const [jobRuns, setJobRuns]         = useState({});   // { [job_id]: [...runs] }
  const [jobArgsModal, setJobArgsModal] = useState(null); // { jobId, startDate, endDate }
  const pollRef = useRef({});                           // { [job_id]: intervalId }

  // Jobs that require date-range args before triggering
  const JOBS_NEED_DATE_ARGS = new Set(['fo_legacy_backfill_nifty50', 'fo_legacy_backfill_banknifty']);

  const fetchJobRuns = useCallback(async (jobId, { force = false } = {}) => {
    const cacheKey = `${jobId}:8`;
    if (!force) {
      const cached = getFreshAdminCache(adminPageCache.jobRuns.get(cacheKey), ADMIN_CACHE_TTLS_MS.jobRuns);
      if (cached) {
        setJobRuns(prev => ({ ...prev, [jobId]: cached }));
        return cached;
      }
    }
    try {
      const r = await axios.get(`${API_URL}/admin/jobs/${jobId}/runs?limit=8`);
      adminPageCache.jobRuns.set(cacheKey, { value: r.data, at: Date.now() });
      setJobRuns(prev => ({ ...prev, [jobId]: r.data }));
      return r.data;
    } catch { return []; }
  }, []);

  const invalidateJobCaches = useCallback((jobId = null) => {
    adminPageCache.jobDashboard = null;
    if (!jobId) {
      adminPageCache.jobRuns.clear();
      return;
    }
    adminPageCache.jobRuns.delete(`${jobId}:8`);
  }, []);

  const fetchData = useCallback(async ({ force = false, background = false } = {}) => {
    if (!force) {
      const cached = getFreshAdminCache(adminPageCache.jobDashboard, ADMIN_CACHE_TTLS_MS.jobDashboard);
      if (cached) {
        setData(cached);
        setLoading(false);
        return cached;
      }
    }
    if (!background && adminPageCache.jobDashboard?.value) {
      setData(adminPageCache.jobDashboard.value);
      setLoading(false);
    } else if (!background) {
      setLoading(true);
    }
    try {
      const r = await axios.get(`${API_URL}/admin/job-runs?days=7`);
      setAdminCache('jobDashboard', r.data);
      setData(r.data);
      return r.data;
    } catch (e) {
      console.error('job-runs fetch failed', e);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  const _startJobPoll = useCallback((jobId) => {
    fetchJobRuns(jobId, { force: true });
    const iv = setInterval(async () => {
      const runs = await fetchJobRuns(jobId, { force: true });
      const latest = runs[0];
      if (!latest || latest.status !== 'running') {
        clearInterval(iv);
        delete pollRef.current[jobId];
        setRunningJob(null);
        fetchData({ force: true });
      }
    }, 3000);
    pollRef.current[jobId] = iv;
  }, [fetchData, fetchJobRuns]); // eslint-disable-line

  const triggerJobWithArgs = useCallback(async (jobId, args = []) => {
    setRunningJob(jobId);
    try {
      await axios.post(`${API_URL}/admin/jobs/${jobId}/run`, { args });
      invalidateJobCaches(jobId);
      _startJobPoll(jobId);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to start job');
      setRunningJob(null);
    }
  }, [_startJobPoll, invalidateJobCaches]); // eslint-disable-line

  const triggerJob = useCallback(async (jobId) => {
    if (JOBS_NEED_DATE_ARGS.has(jobId)) {
      // Show date-range modal instead of firing immediately
      const today = new Date();
      const defaultEnd = today.toISOString().slice(0, 10);
      const defaultStart = new Date(today - 30 * 86400000).toISOString().slice(0, 10);
      setJobArgsModal({ jobId, startDate: defaultStart, endDate: defaultEnd });
      return;
    }
    setRunningJob(jobId);
    try {
      await axios.post(`${API_URL}/admin/jobs/${jobId}/run`);
      invalidateJobCaches(jobId);
      _startJobPoll(jobId);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to start job');
      setRunningJob(null);
    }
  }, [_startJobPoll, JOBS_NEED_DATE_ARGS, invalidateJobCaches]); // eslint-disable-line

  const triggerAllJobs = useCallback(async () => {
    setRunningAll(true);
    try {
      const { data: batch } = await axios.post(`${API_URL}/admin/jobs/run-all`);
      invalidateJobCaches();
      const jobIds = (batch?.jobs || []).map(j => j.job_id);
      if (batch?.skipped_count > 0) {
        console.info('Skipped already-active jobs:', batch.skipped);
      }
      await Promise.all(jobIds.map(jobId => fetchJobRuns(jobId, { force: true })));
      const iv = setInterval(async () => {
        const runsByJob = await Promise.all(jobIds.map(async jobId => {
          const runs = await fetchJobRuns(jobId, { force: true });
          return runs[0];
        }));
          const hasRunning = runsByJob.some(
            latest => latest && (latest.status === 'running' || latest.status === 'queued')
          );
        if (!hasRunning) {
          clearInterval(iv);
          delete pollRef.current.__all__;
          setRunningAll(false);
          fetchData({ force: true });
        }
      }, 3000);
      pollRef.current.__all__ = iv;
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to queue batch');
      setRunningAll(false);
    }
  }, [fetchData, fetchJobRuns, invalidateJobCaches]);

  const clearStuckJobs = useCallback(async () => {
    if (!window.confirm('Clear all running/queued jobs older than 30 minutes? Active batches started within the last 30 minutes will not be affected.')) return;
    setClearingStuck(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/jobs/clear-stuck`);
      invalidateJobCaches();
      if (data.cleared > 0) fetchData({ force: true });
    } catch {}
    finally { setClearingStuck(false); }
  }, [fetchData, invalidateJobCaches]);

  const cancelJob = useCallback(async (jobId) => {
    setCancellingJobs(prev => ({ ...prev, [jobId]: true }));
    try {
      await axios.post(`${API_URL}/admin/jobs/${jobId}/cancel`);
      invalidateJobCaches(jobId);
      await Promise.all([fetchJobRuns(jobId, { force: true }), fetchData({ force: true })]);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setCancellingJobs(prev => ({ ...prev, [jobId]: false }));
      if (pollRef.current[jobId]) {
        clearInterval(pollRef.current[jobId]);
        delete pollRef.current[jobId];
      }
      setRunningJob(prev => (prev === jobId ? null : prev));
    }
  }, [fetchData, fetchJobRuns, invalidateJobCaches]);

  const cancelAllActiveJobs = useCallback(async () => {
    if (!window.confirm('Cancel all active queued/running admin jobs? This will stop the current batch and any manually started active jobs.')) return;
    setCancellingAll(true);
    try {
      await axios.post(`${API_URL}/admin/jobs/cancel-all-active`);
      invalidateJobCaches();
      Object.values(pollRef.current).forEach(iv => clearInterval(iv));
      pollRef.current = {};
      setRunningAll(false);
      setRunningJob(null);
      await fetchData({ force: true });
      if (expandedJob) await fetchJobRuns(expandedJob, { force: true });
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to cancel active jobs');
    } finally {
      setCancellingAll(false);
    }
  }, [expandedJob, fetchData, fetchJobRuns, invalidateJobCaches]);

  // Load run history when a job is expanded
  const handleExpandJob = useCallback((jobId) => {
    setExpandedJob(prev => {
      const next = prev === jobId ? null : jobId;
      if (next) fetchJobRuns(next);
      return next;
    });
  }, [fetchJobRuns]);

  useEffect(() => {
    const cached = getFreshAdminCache(adminPageCache.jobDashboard, ADMIN_CACHE_TTLS_MS.jobDashboard);
    if (cached) {
      setData(cached);
      setLoading(false);
      fetchData({ force: true, background: true });
    } else {
      fetchData();
    }
    return () => {
      Object.values(pollRef.current).forEach(iv => clearInterval(iv));
      pollRef.current = {};
    };
  }, [fetchData]);

  // Tick every second so progress bars animate while jobs are running
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!expandedJob) return undefined;
    const iv = setInterval(async () => {
      const runs = await fetchJobRuns(expandedJob, { force: true });
      if (!runs?.some(run => isActiveRunStatus(run.status))) {
        clearInterval(iv);
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [expandedJob, fetchJobRuns]);

  const getProgress = (ranAt, avgDuration) => {
    if (!ranAt || !avgDuration) return null;
    const elapsed = (Date.now() - new Date(ranAt).getTime()) / 1000;
    return Math.min(95, Math.round((elapsed / avgDuration) * 100));
  };

  if (loading) return <p className="text-sm py-4" style={{ color: colors.secondaryText }}>Loading dashboard…</p>;
  if (!data)   return <p className="text-sm py-4" style={{ color: colors.loss }}>Failed to load job data.</p>;

  const { date_range, jobs } = data;
  const activeJobIds = jobs
    .filter(job => isActiveRunStatus(jobRuns[job.job_id]?.[0]?.status || job.current?.status))
    .map(job => job.job_id);
  const hasActiveJobs = activeJobIds.length > 0;
  // Group by category
  const grouped = {};
  CATEGORY_ORDER.forEach(c => { grouped[c] = []; });
  jobs.forEach(j => { if (grouped[j.category]) grouped[j.category].push(j); });

  // Summary counts for today
  const today = date_range[0];
  const todayJobs = jobs.map(j => j.days.find(d => d.date === today));
  const successToday = todayJobs.filter(d => d?.status === 'success').length;
  const failedToday  = todayJobs.filter(d => d?.status === 'failed').length;
  const partialToday = todayJobs.filter(d => d?.status === 'partial').length;
  const missedToday  = todayJobs.filter(d => d?.status === 'missed').length;
  const noDataToday  = todayJobs.filter(d => d?.status === 'no_data').length;
  const liveCounts = jobs.reduce((acc, job) => {
    const status = jobRuns[job.job_id]?.[0]?.status || job.current?.status || 'no_data';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Alert jobs: those that are missed or failed today with an expected schedule
  const alertJobs = jobs.filter(j => {
    const todayEntry = j.days.find(d => d.date === today);
    return todayEntry?.status === 'missed' || todayEntry?.status === 'failed';
  });

  return (
    <div className="space-y-5 max-w-5xl">

      {/* Header + refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Title row — refresh icon sits here on mobile */}
        <div className="flex items-center justify-between sm:block">
          <div>
            <h2 className="text-base font-bold" style={{ color: colors.primaryText }}>Job Dashboard</h2>
            <p className="text-xs mt-0.5" style={{ color: colors.secondaryText }}>Last 7 days · All times IST</p>
          </div>
          <button onClick={fetchData} className="sm:hidden p-1.5 rounded-lg border"
            style={{ borderColor: colors.border, color: colors.secondaryText }}>
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* Action buttons row — 3 buttons fit on one line on mobile */}
        <div className="flex items-center gap-2">
          <button
            onClick={clearStuckJobs}
            disabled={clearingStuck}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-50"
            style={{ borderColor: '#f9731644', backgroundColor: '#f9731618', color: '#f97316' }}>
            {clearingStuck
              ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />Clearing…</>
              : <><Trash2 className="w-3 h-3" /><span className="hidden xs:inline">Clear Stuck</span></>}
          </button>
          <button
            onClick={triggerAllJobs}
            disabled={runningAll || !!runningJob || cancellingAll}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-50"
            style={{ borderColor: colors.accent + '44', backgroundColor: colors.accent + '18', color: colors.accent }}>
            {runningAll
              ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />Running…</>
              : <><Play className="w-3 h-3" /><span className="hidden xs:inline">Run All Jobs</span></>}
          </button>
          <button
            onClick={cancelAllActiveJobs}
            disabled={!hasActiveJobs || cancellingAll}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-50"
            style={{ borderColor: '#ef444444', backgroundColor: '#ef444418', color: '#ef4444' }}>
            {cancellingAll
              ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />Cancelling…</>
              : <><X className="w-3 h-3" /><span className="hidden xs:inline">Cancel All Active</span></>}
          </button>
          <button onClick={fetchData} className="hidden sm:flex p-1.5 rounded-lg border"
            style={{ borderColor: colors.border, color: colors.secondaryText }}>
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Date-range args modal for fo_legacy_backfill jobs */}
      {jobArgsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setJobArgsModal(null)}>
          <div className="rounded-xl border p-4 w-full max-w-sm shadow-xl" style={{ background: colors.surface || '#1a1f2e', borderColor: colors.border }} onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold mb-3" style={{ color: colors.text }}>
              Date Range — {jobArgsModal.jobId.includes('nifty50') ? 'NIFTY50' : 'BANKNIFTY'}
            </p>
            <label className="block text-xs mb-1" style={{ color: colors.secondaryText }}>Start Date</label>
            <input type="date" value={jobArgsModal.startDate}
              onChange={e => setJobArgsModal(m => ({ ...m, startDate: e.target.value }))}
              className="w-full rounded-lg border px-2 py-1.5 text-xs mb-3"
              style={{ background: colors.background, borderColor: colors.border, color: colors.text }} />
            <label className="block text-xs mb-1" style={{ color: colors.secondaryText }}>End Date</label>
            <input type="date" value={jobArgsModal.endDate}
              onChange={e => setJobArgsModal(m => ({ ...m, endDate: e.target.value }))}
              className="w-full rounded-lg border px-2 py-1.5 text-xs mb-4"
              style={{ background: colors.background, borderColor: colors.border, color: colors.text }} />
            <div className="flex gap-2">
              <button onClick={() => setJobArgsModal(null)}
                className="flex-1 py-1.5 rounded-lg border text-xs"
                style={{ borderColor: colors.border, color: colors.secondaryText }}>Cancel</button>
              <button
                disabled={!jobArgsModal.startDate || !jobArgsModal.endDate}
                onClick={() => {
                  const { jobId, startDate, endDate } = jobArgsModal;
                  setJobArgsModal(null);
                  triggerJobWithArgs(jobId, ['--start-date', startDate, '--end-date', endDate]);
                }}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                style={{ background: colors.accent, color: '#fff' }}>Run</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert banner — shown when any job is failed or missed today */}
      {alertJobs.length > 0 && (
        <div className="rounded-xl border px-4 py-3 flex flex-col gap-1.5"
          style={{ backgroundColor: '#ef444410', borderColor: '#ef444444' }}>
          <p className="text-sm font-bold" style={{ color: '#ef4444' }}>
            {alertJobs.length} job{alertJobs.length > 1 ? 's' : ''} need attention today
          </p>
          <div className="flex flex-wrap gap-2">
            {alertJobs.map(j => {
              const todayEntry = j.days.find(d => d.date === today);
              const isMissed = todayEntry?.status === 'missed';
              return (
                <span key={j.job_id}
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: isMissed ? '#f9731620' : '#ef444420',
                    color: isMissed ? '#f97316' : '#ef4444',
                    border: `1px solid ${isMissed ? '#f9731644' : '#ef444444'}`,
                  }}>
                  {isMissed ? '? Missed' : '✗ Failed'} · {j.job_name}
                  {j.expected_ist ? ` (exp. ${j.expected_ist})` : ''}
                  {j.retry && !isMissed ? ' · auto-retry on' : ''}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats strip — Today + Now in two compact scrollable rows */}
      <div className="space-y-2">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { label: 'Passed',  count: successToday, color: '#16a34a' },
            { label: 'Failed',  count: failedToday,  color: '#ef4444' },
            { label: 'Partial', count: partialToday, color: '#f59e0b' },
            { label: 'Missed',  count: missedToday,  color: '#f97316' },
            { label: 'No Data', count: noDataToday,  color: '#475569' },
          ].map(s => (
            <div key={s.label} className="flex-shrink-0 flex items-center gap-2 rounded-lg border px-3 py-1.5"
              style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
              <span className="text-[15px] font-bold" style={{ color: s.color }}>{s.count}</span>
              <span className="text-[11px] uppercase tracking-wide whitespace-nowrap" style={{ color: colors.secondaryText }}>{s.label} Today</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { label: 'Running',   count: liveCounts.running   || 0, color: '#f59e0b' },
            { label: 'Queued',    count: liveCounts.queued    || 0, color: '#64748b' },
            { label: 'Stale',     count: liveCounts.stale     || 0, color: '#f97316' },
            { label: 'Timed Out', count: liveCounts.timed_out || 0, color: '#dc2626' },
            { label: 'Completed', count: liveCounts.success   || 0, color: '#16a34a' },
          ].map(s => (
            <div key={s.label} className="flex-shrink-0 flex items-center gap-2 rounded-lg border px-3 py-1.5"
              style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
              <span className="text-[15px] font-bold" style={{ color: s.color }}>{s.count}</span>
              <span className="text-[11px] uppercase tracking-wide whitespace-nowrap" style={{ color: colors.secondaryText }}>{s.label} Now</span>
            </div>
          ))}
        </div>
      </div>

      {/* Date header row */}
      <div className="rounded-xl border overflow-x-auto" style={{ borderColor: colors.border }}>
        <div style={{ minWidth: '640px' }}>
        {/* Column headers */}
        <div className="grid border-b px-4 py-2"
          style={{ gridTemplateColumns: '200px 100px repeat(7, 1fr)', borderColor: colors.border, backgroundColor: colors.inputBg }}>
          <span className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Job</span>
          <span className="text-[13px] font-semibold text-center uppercase tracking-wide" style={{ color: colors.secondaryText }}>Current</span>
          {date_range.map((d, i) => (
            <span key={d} className="text-[13px] font-semibold text-center uppercase tracking-wide"
              style={{ color: i === 0 ? colors.accent : colors.secondaryText }}>
              {i === 0 ? 'Today' : new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </span>
          ))}
        </div>

        {/* Rows grouped by category */}
        {CATEGORY_ORDER.map(cat => {
          const catJobs = grouped[cat];
          if (!catJobs.length) return null;
          const catStyle = CATEGORY_COLORS[cat];
          return (
            <div key={cat}>
              {/* Category label */}
              <div className="px-4 py-1.5 flex items-center gap-2"
                style={{ backgroundColor: catStyle.bg }}>
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: catStyle.dot }} />
                <span className="text-[13px] font-bold uppercase tracking-widest" style={{ color: catStyle.dot }}>{cat}</span>
              </div>

              {/* Job rows */}
              {catJobs.map((job, ji) => {
                const isExpanded = expandedJob === job.job_id;
                const meta = JOB_META[job.job_id] || {};
                const rowBg = ji % 2 === 0 ? colors.cardBg : colors.inputBg;
                return (
                  <div key={job.job_id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    {/* Main row — click to expand */}
                    <div
                      className="grid px-4 py-2 items-center cursor-pointer hover:brightness-110 transition-all"
                      style={{ gridTemplateColumns: '200px 100px repeat(7, 1fr)', backgroundColor: rowBg }}
                      onClick={() => handleExpandJob(job.job_id)}
                    >
                      <span className="text-xs pr-2 flex items-center gap-1" style={{ color: colors.primaryText }} title={job.job_name}>
                        <span style={{ color: colors.secondaryText, fontSize: 9 }}>{isExpanded ? '▾' : '▸'}</span>
                        <span className="truncate">{job.job_name}</span>
                      </span>
                      {(() => {
                        const liveRun    = jobRuns[job.job_id]?.[0];
                        const liveCurrent = liveRun?.status;
                        const current = liveCurrent || job.current?.status || 'no_data';
                        const currentStyle = CURRENT_STATUS_STYLE[current] || CURRENT_STATUS_STYLE.no_data;
                        const avgDuration = job.days.find(d => d.avg_duration_s)?.avg_duration_s;
                        const startedAt = liveRun?.started_at || job.current?.started_at || liveRun?.ran_at || job.current?.ran_at;
                        const pct = liveRun?.progress_pct ?? job.current?.progress_pct ?? (current === 'running' ? getProgress(startedAt, avgDuration) : null);
                        const statusMeta = liveRun?.progress_message || job.current?.progress_message;
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className="px-2 py-1 rounded text-[11px] font-semibold whitespace-nowrap"
                              style={{
                                backgroundColor: currentStyle.bg + '22',
                                color: currentStyle.bg === '#1e293b' ? '#64748b' : currentStyle.bg,
                                border: `1px solid ${currentStyle.bg}44`,
                              }}
                            >
                              {currentStyle.label}
                            </span>
                            {pct !== null && isActiveRunStatus(current) && (
                              <div className="w-full px-1">
                                <div className="w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#f59e0b22' }}>
                                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: '#f59e0b' }} />
                                </div>
                                <p className="text-center text-[10px] mt-0.5" style={{ color: '#f59e0b' }}>{pct}%</p>
                              </div>
                            )}
                            {statusMeta && statusMeta !== currentStyle.label && (
                              <p className="text-center text-[10px] px-1" style={{ color: colors.secondaryText }}>{statusMeta}</p>
                            )}
                          </div>
                        );
                      })()}
                      {job.days.map(day => {
                        const st = STATUS_STYLE[day.status] || STATUS_STYLE.no_data;
                        return (
                          <div key={day.date} className="flex justify-center">
                            <button
                              className="w-6 h-6 rounded flex items-center justify-center text-[13px] font-bold transition-all hover:scale-110"
                              style={{ backgroundColor: st.bg + '33', color: st.bg === '#1e293b' ? '#475569' : st.bg, border: `1px solid ${st.bg}55` }}
                              onMouseEnter={e => { e.stopPropagation(); setTooltip({ job, day, rect: e.currentTarget.getBoundingClientRect() }); }}
                              onMouseLeave={() => setTooltip(null)}
                              onClick={e => e.stopPropagation()}
                            >
                              {st.label}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Expanded metadata + run history row */}
                    {isExpanded && (
                      <div className="px-6 py-3 space-y-3"
                        style={{ backgroundColor: colors.inputBg, borderTop: `1px dashed ${colors.border}` }}>
                        {/* Summary */}
                        {meta.summary && (
                          <p className="text-xs leading-relaxed" style={{ color: colors.secondaryText }}>{meta.summary}</p>
                        )}
                        {/* Meta grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs">
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Schedule (IST)</span>
                            <p className="mt-0.5 font-mono" style={{ color: colors.primaryText }}>{meta.schedule || '—'}</p>
                          </div>
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Source File</span>
                            <p className="mt-0.5 font-mono" style={{ color: '#60a5fa' }}>{meta.file || '—'}</p>
                          </div>
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Source Data</span>
                            <p className="mt-0.5" style={{ color: colors.primaryText }}>{meta.source || '—'}</p>
                          </div>
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Destination</span>
                            <p className="mt-0.5" style={{ color: '#34d399' }}>{meta.destination || '—'}</p>
                          </div>
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Current State</span>
                            <p className="mt-0.5" style={{ color: colors.primaryText }}>
                              {(jobRuns[job.job_id]?.[0]?.status || job.current?.status || 'no_data').replace('_', ' ')}
                            </p>
                          </div>
                          <div>
                            <span className="text-[12px] uppercase tracking-wide font-semibold" style={{ color: colors.secondaryText }}>Last Heartbeat</span>
                            <p className="mt-0.5" style={{ color: colors.primaryText }}>
                              {formatJobDateTime(jobRuns[job.job_id]?.[0]?.heartbeat_at || job.current?.heartbeat_at)}
                            </p>
                          </div>
                        </div>

                        {/* Run now button */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={e => { e.stopPropagation(); triggerJob(job.job_id); }}
                            disabled={!job.runnable || runningJob === job.job_id || runningAll || cancellingAll || isActiveRunStatus(jobRuns[job.job_id]?.[0]?.status || job.current?.status)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50"
                            style={{ backgroundColor: colors.accent + '22', color: colors.accent, border: `1px solid ${colors.accent}44` }}>
                            {!job.runnable
                              ? <>Not Runnable</>
                              : runningJob === job.job_id
                              ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />Running…</>
                              : <><Play className="w-3 h-3" />Run Now</>}
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); cancelJob(job.job_id); }}
                            disabled={!isActiveRunStatus(jobRuns[job.job_id]?.[0]?.status || job.current?.status) || !!cancellingJobs[job.job_id] || cancellingAll}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50"
                            style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444444' }}>
                            {cancellingJobs[job.job_id]
                              ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />Cancelling…</>
                              : <><X className="w-3 h-3" />Cancel</>}
                          </button>
                        </div>

                        {/* Run history */}
                        {(jobRuns[job.job_id] || []).length > 0 && (
                          <div>
                            <p className="text-[12px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: colors.secondaryText }}>Run History</p>
                            <div className="space-y-1">
                              {(jobRuns[job.job_id] || []).map((run, idx) => {
                                const isRunning = run.status === 'running';
                                const isQueued = run.status === 'queued';
                                const isCancelled = run.status === 'cancelled';
                                const isFailed  = run.status === 'failed' || run.status === 'timed_out' || run.status === 'stale';
                                const statusColor = isRunning ? '#f59e0b' : isQueued ? '#64748b' : isCancelled ? '#94a3b8' : isFailed ? (run.status === 'stale' ? '#f97316' : '#ef4444') : '#16a34a';
                                const ts = formatJobDateTime(run.ran_at);
                                const avgDuration = job.days.find(d => d.avg_duration_s)?.avg_duration_s;
                                const pct = run.progress_pct ?? (isRunning ? getProgress(run.started_at || run.ran_at, avgDuration) : null);
                                return (
                                  <div key={run.run_id} className="rounded px-2.5 py-1.5 text-[14px] flex flex-col gap-0.5"
                                    style={{ backgroundColor: colors.cardBg, border: `1px solid ${statusColor}33` }}>
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold" style={{ color: statusColor }}>
                                        {isRunning ? '⟳ Running…' : isQueued ? '• Queued' : isCancelled ? '■ Cancelled' : isFailed ? `✕ ${CURRENT_STATUS_STYLE[run.status]?.label || 'Failed'}` : '✓ Success'}
                                        {idx === 0 && <span className="ml-1.5 text-[12px] px-1 py-0.5 rounded" style={{ backgroundColor: colors.accent + '22', color: colors.accent }}>latest</span>}
                                      </span>
                                      <span style={{ color: colors.secondaryText }}>{ts}{run.duration_s ? ` · ${run.duration_s.toFixed(1)}s` : ''}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: colors.secondaryText }}>
                                      <span>Queued: {formatJobAge(run.queued_at)} ago</span>
                                      <span>Heartbeat: {formatJobAge(run.heartbeat_at)} ago</span>
                                      <span>Source: {run.trigger_source || '—'}</span>
                                      <span>Runner: {run.runner_type || '—'}</span>
                                    </div>
                                    {(isRunning || isQueued) && pct !== null && (
                                      <div className="mt-1">
                                        <div className="w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#f59e0b22' }}>
                                          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: '#f59e0b' }} />
                                        </div>
                                        <p className="text-[10px] mt-0.5" style={{ color: '#f59e0b' }}>
                                          {pct}% · {run.progress_message || (avgDuration ? `est. ${Math.round(avgDuration)}s total` : 'estimating…')}
                                        </p>
                                      </div>
                                    )}
                                    {isFailed && (run.error || run.stale_reason) && (
                                      <p className="font-mono text-[13px] mt-0.5 whitespace-pre-wrap break-words" style={{ color: '#fca5a5' }} title={run.error || run.stale_reason}>
                                        {run.error || run.stale_reason}
                                      </p>
                                    )}
                                    {run.logs && (
                                      <details className="mt-1">
                                        <summary className="cursor-pointer text-[12px]" style={{ color: colors.accent }}>
                                          View logs
                                        </summary>
                                        <pre
                                          className="mt-2 rounded-lg p-3 overflow-auto text-[11px] whitespace-pre-wrap"
                                          style={{
                                            maxHeight: 260,
                                            backgroundColor: '#020617',
                                            color: '#cbd5e1',
                                            border: `1px solid ${colors.border}`,
                                          }}
                                        >
                                          {run.logs}
                                        </pre>
                                      </details>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        </div>{/* end minWidth wrapper */}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[13px]" style={{ color: colors.secondaryText }}>
        {Object.entries(STATUS_STYLE).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1">
            <span className="w-3 h-3 rounded inline-flex items-center justify-center font-bold text-[11px]"
              style={{ backgroundColor: v.bg + '33', color: v.bg === '#1e293b' ? '#475569' : v.bg, border: `1px solid ${v.bg}55` }}>
              {v.label}
            </span>
            {k.charAt(0).toUpperCase() + k.slice(1).replace('_', ' ')}
          </span>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.day.status !== 'no_data' && (
        <div className="app-card fixed z-50 pointer-events-none px-3 py-2 rounded-lg text-xs shadow-lg border"
          style={{
            top: tooltip.rect.top - 10,
            left: tooltip.rect.left + tooltip.rect.width / 2,
            transform: 'translate(-50%, -100%)',
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
            color: colors.primaryText,
            minWidth: 200,
          }}>
          <p className="font-bold mb-1">{tooltip.job.job_name}</p>
          <p style={{ color: colors.secondaryText }}>{tooltip.day.date}</p>
          {tooltip.job.expected_ist && (
            <p style={{ color: colors.secondaryText }}>Expected: {tooltip.job.expected_ist} IST</p>
          )}
          {tooltip.day.status === 'missed' && (
            <p style={{ color: '#f97316' }}>Job did not run — scheduler may have missed the window</p>
          )}
          {tooltip.day.success_count != null && <p>✓ {tooltip.day.success_count} success · ✗ {tooltip.day.failed_count} failed</p>}
          {tooltip.day.avg_duration_s != null && <p>Avg duration: {tooltip.day.avg_duration_s}s</p>}
          {tooltip.day.last_error && <p className="mt-1 text-red-400 truncate max-w-[220px]">{tooltip.day.last_error}</p>}
          {tooltip.job.retry && tooltip.day.status === 'failed' && (
            <p className="mt-1" style={{ color: '#f59e0b' }}>Auto-retry enabled</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Log Monitor (raw viewer) ─────────────────────────────────────────────────

// Values match the SERVICE_NAME env var set per container in docker-compose
const SERVICES = ['', 'api', 'worker'];
const LEVELS = ['', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const LEVEL_COLOR = {
  ERROR:   { bg: '#ef444415', text: '#ef4444', border: '#ef444440' },
  WARN:    { bg: '#f9731615', text: '#f97316', border: '#f9731640' },
  INFO:    { bg: '#22c55e15', text: '#22c55e', border: '#22c55e40' },
  DEBUG:   { bg: '#94a3b815', text: '#94a3b8', border: '#94a3b840' },
  DEFAULT: { bg: '#6366f115', text: '#818cf8', border: '#6366f140' },
};

const SVC_COLORS = [
  '#6366f1','#22c55e','#f59e0b','#ec4899','#14b8a6','#f97316','#8b5cf6','#06b6d4',
];
const svcColor = (svc) => SVC_COLORS[Math.abs([...svc].reduce((a,c)=>a+c.charCodeAt(0),0)) % SVC_COLORS.length];

function LogMonitor({ colors }) {
  const [mHours,    setMHours]    = useState(1);
  const [mLimit,    setMLimit]    = useState(500);
  const [mService,  setMService]  = useState('');
  const [mLevel,    setMLevel]    = useState('');
  const [mSearch,   setMSearch]   = useState('');
  const [mLogs,     setMLogs]     = useState([]);
  const [mLoading,  setMLoading]  = useState(false);
  const [mError,    setMError]    = useState(null);
  const [mFetchedAt,setMFetchedAt]= useState(null);
  const [autoRef,   setAutoRef]   = useState(false);
  const autoTimer = useRef(null);
  const logEndRef  = useRef(null);

  const fetchLogs = useCallback(async () => {
    setMLoading(true);
    setMError(null);
    try {
      const params = new URLSearchParams({ hours: mHours, limit: mLimit });
      if (mService) params.append('service', mService);
      if (mLevel)   params.append('level',   mLevel);
      if (mSearch)  params.append('search',  mSearch);
      const r = await axios.get(`${API_URL}/admin/logs?${params}`);
      setMLogs(r.data.logs || []);
      setMFetchedAt(r.data.fetched_at);
      setMError(r.data.error || null);
    } catch (e) {
      setMLogs([]);
      setMError('Log service unreachable — OpenObserve may be down.');
    } finally {
      setMLoading(false);
    }
  }, [mHours, mLimit, mService, mLevel, mSearch]);

  useEffect(() => {
    if (autoRef) {
      autoTimer.current = setInterval(fetchLogs, 30000);
    } else {
      clearInterval(autoTimer.current);
    }
    return () => clearInterval(autoTimer.current);
  }, [autoRef, fetchLogs]);

  useEffect(() => {
    if (mLogs.length) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mLogs]);

  const levelStyle = (lvl) => LEVEL_COLOR[lvl] || LEVEL_COLOR.DEFAULT;

  const handleKeyDown = (e) => { if (e.key === 'Enter') fetchLogs(); };

  return (
    <div className="flex flex-col gap-3">
      {/* Controls */}
      <div className="rounded-xl p-4 flex flex-wrap items-end gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Time range</label>
          <select value={mHours} onChange={e => setMHours(Number(e.target.value))}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Lines</label>
          <select value={mLimit} onChange={e => setMLimit(Number(e.target.value))}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1,000</option>
            <option value={2000}>2,000</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Service</label>
          <select value={mService} onChange={e => setMService(e.target.value)}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
            {SERVICES.map(s => <option key={s} value={s}>{s || 'All services'}</option>)}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Level</label>
          <select value={mLevel} onChange={e => setMLevel(e.target.value)}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
            {LEVELS.map(l => <option key={l} value={l}>{l || 'All levels'}</option>)}
          </select>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Keyword search</label>
          <input
            type="text"
            value={mSearch}
            onChange={e => setMSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. job_completed, order_fill, api_request…"
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
          />
        </div>

        <div className="flex items-center gap-2 self-end pb-0.5">
          <button onClick={fetchLogs} disabled={mLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            style={{ backgroundColor: colors.accent, color: '#fff', opacity: mLoading ? 0.6 : 1 }}>
            <RefreshCw className={`w-3 h-3 ${mLoading ? 'animate-spin' : ''}`} />
            {mLoading ? 'Loading…' : 'Fetch'}
          </button>

          <button onClick={() => setAutoRef(a => !a)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            style={{ backgroundColor: autoRef ? '#22c55e20' : colors.inputBg, color: autoRef ? '#22c55e' : colors.secondaryText, border: `1px solid ${autoRef ? '#22c55e60' : colors.border}` }}>
            <Clock className="w-3 h-3" />
            {autoRef ? 'Auto ON' : 'Auto'}
          </button>
        </div>

        {mFetchedAt && (
          <span className="text-xs self-end pb-1 ml-auto" style={{ color: colors.secondaryText }}>
            {mLogs.length} lines · {new Date(mFetchedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {mError && (
        <div className="rounded-xl p-3 flex items-center gap-2 text-xs" style={{ backgroundColor: '#f9731615', border: '1px solid #f9731640', color: '#f97316' }}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          {mError}
        </div>
      )}

      {/* Log viewer */}
      {mLogs.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#0d1117', border: `1px solid ${colors.border}` }}>
          <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: '#161b22' }}>
            <span className="text-xs font-mono font-semibold" style={{ color: '#7d8590' }}>LOGS — {mLogs.length} entries</span>
            <div className="flex gap-1">
              {['ERROR','WARN','INFO','DEBUG'].map(l => {
                const cnt = mLogs.filter(x => x.level === l).length;
                if (!cnt) return null;
                const st = levelStyle(l);
                return (
                  <span key={l} className="text-xs px-1.5 py-0.5 rounded font-mono"
                    style={{ backgroundColor: st.bg, color: st.text, border: `1px solid ${st.border}` }}>
                    {l} {cnt}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="overflow-y-auto font-mono text-xs" style={{ maxHeight: '520px' }}>
            {mLogs.map((log, i) => {
              const st = levelStyle(log.level);
              return (
                <div key={i} className="flex items-start gap-2 px-3 py-1.5 hover:bg-white/5 border-b border-white/5">
                  <span className="flex-shrink-0 opacity-50 text-xs" style={{ color: '#7d8590', minWidth: '60px' }}>{log.ts}</span>
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-bold leading-none"
                    style={{ backgroundColor: `${svcColor(log.service)}20`, color: svcColor(log.service), minWidth: '80px', textAlign: 'center', fontSize: '0.6rem' }}>
                    {log.service}
                  </span>
                  <span className="flex-shrink-0 px-1 py-0.5 rounded text-xs font-bold leading-none"
                    style={{ backgroundColor: st.bg, color: st.text, minWidth: '40px', textAlign: 'center', fontSize: '0.6rem' }}>
                    {log.level}
                  </span>
                  <span className="flex-1 break-all leading-relaxed" style={{ color: log.level === 'ERROR' ? '#fca5a5' : log.level === 'WARN' ? '#fdba74' : '#cdd9e5' }}>
                    {mSearch ? (
                      log.message.split(new RegExp(`(${mSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')).map((part, j) =>
                        part.toLowerCase() === mSearch.toLowerCase()
                          ? <mark key={j} style={{ backgroundColor: '#fbbf2480', color: '#fbbf24', borderRadius: '2px' }}>{part}</mark>
                          : part
                      )
                    ) : log.message}
                  </span>
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {!mLoading && mLogs.length === 0 && mFetchedAt && (
        <div className="rounded-xl p-4 flex items-center justify-center gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <Info className="w-4 h-4" style={{ color: colors.secondaryText }} />
          <p className="text-sm" style={{ color: colors.secondaryText }}>No logs found for the selected filters.</p>
        </div>
      )}

      {mLogs.length === 0 && !mFetchedAt && (
        <div className="rounded-xl p-4 flex items-center justify-center gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <Activity className="w-4 h-4" style={{ color: colors.secondaryText }} />
          <p className="text-sm" style={{ color: colors.secondaryText }}>Set filters and click Fetch to load logs.</p>
        </div>
      )}
    </div>
  );
}

// ─── AI Log Analysis Tab ──────────────────────────────────────────────────────

function LogAnalysisTab({ colors }) {
  const [hours, setHours] = useState(24);
  const [limit, setLimit] = useState(1000);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/admin/ai-log-analysis`, { hours, limit });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const severityColor = (s) => {
    if (s === 'critical') return '#ef4444';
    if (s === 'high')     return '#f97316';
    return '#eab308';
  };

  const SeverityIcon = ({ s }) => {
    if (s === 'critical') return <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#ef4444' }} />;
    if (s === 'high')     return <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#f97316' }} />;
    return <Info className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#eab308' }} />;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Controls */}
      <div className="rounded-xl p-4 flex flex-wrap items-end gap-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Time range</label>
          <select
            value={hours}
            onChange={e => setHours(Number(e.target.value))}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={72}>Last 72 hours</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Log lines</label>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            className="text-xs px-2 py-1.5 rounded"
            style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
          >
            <option value={500}>500 lines</option>
            <option value={1000}>1,000 lines</option>
            <option value={2000}>2,000 lines</option>
            <option value={5000}>5,000 lines</option>
          </select>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-1.5 rounded text-xs font-semibold"
          style={{ backgroundColor: colors.accent, color: '#fff', opacity: loading ? 0.6 : 1 }}
        >
          <Brain className="w-3.5 h-3.5" />
          {loading ? 'Analyzing…' : 'Analyze Logs'}
        </button>
        {result && (
          <span className="text-xs ml-auto" style={{ color: colors.secondaryText }}>
            {result.log_count} lines analyzed · {new Date(result.analyzed_at).toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-xl p-3 text-xs" style={{ backgroundColor: '#ef444415', border: '1px solid #ef4444', color: '#ef4444' }}>
          {error}
        </div>
      )}

      {loading && (
        <div className="rounded-xl p-4 flex flex-col items-center gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <Brain className="w-8 h-8 animate-pulse" style={{ color: colors.accent }} />
          <p className="text-sm" style={{ color: colors.secondaryText }}>Analysing your logs…</p>
        </div>
      )}

      {result && !loading && (
        <div className="flex flex-col gap-3">
          {/* Summary */}
          <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <p className="text-xs font-semibold mb-1" style={{ color: colors.accent }}>System Health Summary</p>
            <p className="text-sm" style={{ color: colors.primaryText }}>{result.summary}</p>
          </div>

          <div className="grid grid-cols-1 gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {/* Issues */}
            {result.issues?.length > 0 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                <p className="text-xs font-semibold mb-3" style={{ color: colors.primaryText }}>Issues Found ({result.issues.length})</p>
                <div className="flex flex-col gap-2">
                  {result.issues.map((issue, i) => (
                    <div key={i} className="rounded-lg p-3" style={{ backgroundColor: `${severityColor(issue.severity)}10`, border: `1px solid ${severityColor(issue.severity)}40` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <SeverityIcon s={issue.severity} />
                        <span className="text-xs font-semibold" style={{ color: severityColor(issue.severity) }}>{issue.severity?.toUpperCase()}</span>
                        <span className="text-xs" style={{ color: colors.secondaryText }}>{issue.service}</span>
                      </div>
                      <p className="text-xs" style={{ color: colors.primaryText }}>{issue.description}</p>
                      {issue.log_evidence && (
                        <p className="text-xs mt-1 font-mono opacity-70 truncate" style={{ color: colors.secondaryText }}>"{issue.log_evidence}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Anomalies */}
            {result.anomalies?.length > 0 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                <p className="text-xs font-semibold mb-3" style={{ color: colors.primaryText }}>Anomalies ({result.anomalies.length})</p>
                <div className="flex flex-col gap-2">
                  {result.anomalies.map((a, i) => (
                    <div key={i} className="rounded-lg p-3" style={{ backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.border}` }}>
                      <p className="text-xs font-medium" style={{ color: colors.accent }}>{a.service}</p>
                      <p className="text-xs mt-0.5" style={{ color: colors.primaryText }}>{a.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                <p className="text-xs font-semibold mb-3" style={{ color: colors.primaryText }}>Recommendations</p>
                <div className="flex flex-col gap-2">
                  {result.recommendations.map((r, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                      <p className="text-xs" style={{ color: colors.primaryText }}>{r}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Silent Services */}
            {result.silent_services?.length > 0 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
                <p className="text-xs font-semibold mb-3" style={{ color: colors.primaryText }}>Silent Services (no logs)</p>
                <div className="flex flex-wrap gap-2">
                  {result.silent_services.map((s, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#f9731620', color: '#f97316', border: '1px solid #f9731640' }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {result.issues?.length === 0 && result.anomalies?.length === 0 && (
            <div className="rounded-xl p-6 flex items-center gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
              <CheckCircle className="w-5 h-5" style={{ color: '#22c55e' }} />
              <p className="text-sm" style={{ color: colors.primaryText }}>No issues or anomalies detected in the analyzed logs.</p>
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3 mt-2">
        <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
        <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Log Monitor</span>
        <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
      </div>

      <LogMonitor colors={colors} />
    </div>
  );
}

// ─── Banner Tab ───────────────────────────────────────────────────────────────

function BannerTab({ colors }) {
  const [banner, setBanner] = useState(null);
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchBanner = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/banner`);
      setBanner(data?.active ? data : null);
      if (data?.active) {
        setMessage(data.message);
        setType(data.type || 'info');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchBanner(); }, [fetchBanner]);

  const save = async () => {
    if (!message.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/banner`, { message: message.trim(), type });
      setBanner(data);
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API_URL}/admin/banner`);
      setBanner(null);
      setMessage('');
      setType('info');
    } finally { setClearing(false); }
  };

  const TYPE_OPTIONS = [
    { value: 'info',    label: 'Info',    color: '#93c5fd' },
    { value: 'warning', label: 'Warning', color: '#fcd34d' },
    { value: 'error',   label: 'Error',   color: '#fca5a5' },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-xl">
      {/* Current banner status */}
      <div className="rounded-xl p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <p className="text-xs font-semibold mb-2" style={{ color: colors.secondaryText }}>CURRENT BANNER</p>
        {banner ? (
          <div className="flex items-start gap-3">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
              style={{
                backgroundColor: banner.type === 'error' ? 'rgba(239,68,68,0.15)' : banner.type === 'warning' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                color: banner.type === 'error' ? '#fca5a5' : banner.type === 'warning' ? '#fcd34d' : '#93c5fd',
              }}>
              {banner.type}
            </span>
            <p className="text-sm flex-1" style={{ color: colors.primaryText }}>{banner.message}</p>
          </div>
        ) : (
          <p className="text-xs" style={{ color: colors.secondaryText }}>No active banner</p>
        )}
      </div>

      {/* Banner editor */}
      <div className="rounded-xl p-4 flex flex-col gap-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>SET BANNER</p>

        {/* Type selector */}
        <div className="flex gap-2">
          {TYPE_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setType(opt.value)}
              className="px-3 py-1 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: type === opt.value ? `${opt.color}20` : colors.inputBg,
                border: `1px solid ${type === opt.value ? opt.color : colors.border}`,
                color: type === opt.value ? opt.color : colors.secondaryText,
              }}>
              {opt.label}
            </button>
          ))}
        </div>

        {/* Message input */}
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Enter banner message…"
          rows={3}
          className="w-full text-sm rounded-lg px-3 py-2 resize-none outline-none"
          style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText }}
        />

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={save} disabled={saving || !message.trim()}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{ backgroundColor: colors.accent, color: '#fff', opacity: (!message.trim() || saving) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : banner ? 'Update Banner' : 'Publish Banner'}
          </button>
          {banner && (
            <button onClick={clear} disabled={clearing}
              className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)' }}>
              {clearing ? 'Clearing…' : 'Clear'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────

// ─── ACT Scenarios Tab ────────────────────────────────────────────────────────

const OUTCOME_COLOR = (pct) => pct == null ? '#6b7280' : pct >= 5 ? '#16C784' : pct >= 0 ? '#f59e0b' : '#ef4444';
const TREND_ICON = (t) => t === 'UPTREND' ? <TrendingUp className="w-3 h-3 text-emerald-400 inline" /> : t === 'DOWNTREND' ? <TrendingDown className="w-3 h-3 text-red-400 inline" /> : <Minus className="w-3 h-3 text-gray-400 inline" />;

const STATUS_CONFIG = {
  IN_TODAY_SET:   { label: "Today's Set", color: '#16C784', bg: '#16C78420' },
  RECENTLY_USED:  { label: 'Recently Used', color: '#f59e0b', bg: '#f59e0b20' },
  AVAILABLE:      { label: 'Available', color: '#6366f1', bg: '#6366f120' },
};

function UpcomingDateCard({ dateStr, byType, todayStr, colors, qColor, openPreview }) {
  const isToday = dateStr === todayStr;
  const allScenarios = [...(byType.SWING || []), ...(byType.INTRADAY || [])];
  const avgQ = allScenarios.length
    ? Math.round(allScenarios.reduce((s, r) => s + (r.quality_score || 0), 0) / allScenarios.length)
    : null;
  return (
    <div className="rounded-lg mb-3 overflow-hidden"
      style={{ border: `1px solid ${isToday ? '#16C784' : colors.border}`, backgroundColor: colors.cardBg }}>
      <div className="flex items-center justify-between px-3 py-2"
        style={{ backgroundColor: isToday ? 'rgba(22,199,132,0.1)' : colors.inputBg, borderBottom: `1px solid ${colors.border}` }}>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[13px]" style={{ color: isToday ? '#16C784' : colors.primaryText }}>{dateStr}</span>
          {isToday && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#16C784', color: '#000' }}>TODAY</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px]" style={{ color: colors.secondaryText }}>
            {(byType.SWING?.length || 0)} swing · {(byType.INTRADAY?.length || 0)} intraday
          </span>
          {avgQ != null && <span className="text-[11px] font-bold" style={{ color: qColor(avgQ) }}>avg Q: {avgQ}</span>}
        </div>
      </div>
      {['SWING', 'INTRADAY'].map(tt => {
        const rows = byType[tt] || [];
        if (!rows.length) return null;
        return (
          <div key={tt} className="px-3 py-2">
            <p className="text-[10px] font-semibold mb-1.5 uppercase tracking-wide"
              style={{ color: tt === 'SWING' ? '#818cf8' : '#f59e0b' }}>{tt}</p>
            <div className="flex flex-col gap-1">
              {rows.map(s => (
                <div key={s.scenario_id}
                  className="flex items-center justify-between rounded px-2 py-1 cursor-pointer hover:opacity-80"
                  style={{ backgroundColor: colors.inputBg }}
                  onClick={() => openPreview(s.scenario_id)}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-[12px]" style={{ color: colors.primaryText }}>{s.symbol}</span>
                    <span className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{s.scanner_name?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px]" style={{ color: s.exit_type === 'TARGET' ? '#16C784' : s.exit_type === 'STOP' ? '#ef4444' : colors.secondaryText }}>
                      {s.exit_type} {s.outcome_pct != null ? `${s.outcome_pct > 0 ? '+' : ''}${s.outcome_pct}%` : ''}
                    </span>
                    <span className="text-[11px] font-bold w-8 text-right" style={{ color: qColor(s.quality_score) }}>
                      {s.quality_score != null ? s.quality_score : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ACTScenariosTab({ colors }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [fetchError, setFetchError]   = useState(null);
  const [preview, setPreview]         = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError]     = useState(null);
  const [page, setPage]               = useState(1);
  const [filters, setFilters]         = useState({ trade_type: '', scanner: '', symbol: '', date_from: '', date_to: '', status: '' });
  const [activeView, setActiveView]   = useState('pool'); // 'pool' | 'upcoming'
  const [upcomingData, setUpcomingData] = useState(null);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingDays, setUpcomingDays] = useState(14);

  const token = localStorage.getItem('token');

  const fetchScenarios = useCallback(async (p = page, f = filters) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams({ page: p, page_size: 50 });
      if (f.trade_type) params.set('trade_type', f.trade_type);
      if (f.scanner)    params.set('scanner', f.scanner);
      if (f.symbol)     params.set('symbol', f.symbol);
      if (f.date_from)  params.set('date_from', f.date_from);
      if (f.date_to)    params.set('date_to', f.date_to);
      if (f.status)     params.set('status', f.status);
      const res = await axios.get(`${API_URL}/admin/act/scenarios?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setFetchError(e.response?.data?.detail || 'Failed to load scenarios. Check backend.');
    }
    finally { setLoading(false); }
  }, [page, filters, token]);

  useEffect(() => { fetchScenarios(); }, []); // eslint-disable-line

  const fetchUpcoming = useCallback(async (days = upcomingDays) => {
    setUpcomingLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/act/schedule?upcoming=true&days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      // Group by date, preserve sorted order
      const grouped = {};
      for (const row of res.data.items) {
        if (!grouped[row.set_date]) grouped[row.set_date] = { SWING: [], INTRADAY: [] };
        grouped[row.set_date][row.trade_type].push(row);
      }
      setUpcomingData(grouped);
    } catch (e) {
      console.error(e);
    } finally {
      setUpcomingLoading(false);
    }
  }, [token, upcomingDays]);

  useEffect(() => { if (activeView === 'upcoming') fetchUpcoming(); }, [activeView]); // eslint-disable-line

  const openPreview = async (id) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview({ id });
    try {
      const res = await axios.get(`${API_URL}/admin/act/scenarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(res.data);
    } catch (e) {
      console.error(e);
      setPreviewError('Failed to load scenario detail.');
    }
    finally { setPreviewLoading(false); }
  };

  const applyFilters = () => { setPage(1); fetchScenarios(1, filters); };
  const clearFilters = () => { const f = { trade_type: '', scanner: '', symbol: '', date_from: '', date_to: '', status: '' }; setFilters(f); setPage(1); fetchScenarios(1, f); };
  const goPage = (p) => { setPage(p); fetchScenarios(p, filters); };
  const quickFilter = (statusVal) => { const f = { ...filters, status: statusVal }; setFilters(f); setPage(1); fetchScenarios(1, f); };

  const inputStyle = { backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText, borderRadius: 6, padding: '4px 8px', fontSize: 12 };

  const today = new Date().toISOString().slice(0, 10);
  const qColor = v => v == null ? colors.secondaryText : v >= 70 ? '#16C784' : v >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col xl:flex-row gap-3 h-full" style={{ minHeight: 0 }}>
      {/* Left: table */}
      <div className="flex-1 flex flex-col gap-3 min-w-0">

        {/* Sub-tab toggle */}
        <div className="flex flex-wrap gap-2 items-center">
          {['pool', 'upcoming'].map(v => (
            <button key={v}
              onClick={() => setActiveView(v)}
              className="px-3 py-1 rounded text-[12px] font-semibold"
              style={{ backgroundColor: activeView === v ? '#6366f1' : colors.inputBg, color: activeView === v ? '#fff' : colors.secondaryText, border: `1px solid ${activeView === v ? '#6366f1' : colors.border}` }}>
              {v === 'pool' ? 'Scenario Pool' : 'Upcoming Sets'}
            </button>
          ))}
          {activeView === 'upcoming' && (
            <div className="flex flex-wrap items-center gap-2 ml-0 sm:ml-2">
              <span className="text-[11px]" style={{ color: colors.secondaryText }}>Next</span>
              <select value={upcomingDays} onChange={e => { setUpcomingDays(+e.target.value); fetchUpcoming(+e.target.value); }}
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText, borderRadius: 4, padding: '2px 6px', fontSize: 12 }}>
                {[7, 14, 30, 60].map(d => <option key={d} value={d}>{d} days</option>)}
              </select>
              <button onClick={() => fetchUpcoming(upcomingDays)} className="text-[11px] px-2 py-0.5 rounded"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.secondaryText }}>↻ Refresh</button>
            </div>
          )}
        </div>

        {/* ── Upcoming Sets view ── */}
        {activeView === 'upcoming' && (
          <div className="flex-1 overflow-y-auto pr-1" style={{ minHeight: 0 }}>
            {upcomingLoading && <p className="text-[12px] text-center py-8" style={{ color: colors.secondaryText }}>Loading upcoming sets…</p>}
            {!upcomingLoading && upcomingData && Object.keys(upcomingData).length === 0 && (
              <p className="text-[12px] text-center py-8" style={{ color: colors.secondaryText }}>No sets pre-allocated yet. Run ACT Daily Allocate job.</p>
            )}
            {!upcomingLoading && upcomingData && Object.keys(upcomingData).sort().map(dateStr => (
              <UpcomingDateCard
                key={dateStr}
                dateStr={dateStr}
                byType={upcomingData[dateStr]}
                todayStr={today}
                colors={colors}
                qColor={qColor}
                openPreview={openPreview}
              />
            ))}
          </div>
        )}

        {/* ── Pool view ── */}

        {activeView === 'pool' && <>

        {/* Summary cards — 6 cards: pool counts + rotation health */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
            {[
              { label: 'Total',        value: data.summary.total,        color: colors.primaryText,  onClick: () => quickFilter('') },
              { label: 'Swing',        value: data.summary.swing,        color: '#818cf8',            onClick: null },
              { label: 'Intraday',     value: data.summary.intraday,     color: '#f59e0b',            onClick: null },
              { label: "Today's Set",  value: data.summary.in_today_set, color: '#16C784',            onClick: () => quickFilter('IN_TODAY_SET') },
              { label: 'Recent 60d',   value: data.summary.recently_used,color: '#f59e0b',            onClick: () => quickFilter('RECENTLY_USED') },
              { label: 'Available',    value: data.summary.available,    color: '#6366f1',            onClick: () => quickFilter('AVAILABLE') },
            ].map(({ label, value, color, onClick }) => (
              <div key={label}
                className={`rounded-lg p-2 text-center ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
                style={{ backgroundColor: colors.cardBg, border: `1px solid ${filters.status === (label === "Today's Set" ? 'IN_TODAY_SET' : label === 'Recent 60d' ? 'RECENTLY_USED' : label === 'Available' ? 'AVAILABLE' : '') && label !== 'Total' && label !== 'Swing' && label !== 'Intraday' ? color : colors.border}` }}
                onClick={onClick || undefined}>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>{label}</p>
                <p className="text-base font-bold" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-end">
          <select style={inputStyle} value={filters.trade_type} onChange={e => setFilters(f => ({ ...f, trade_type: e.target.value }))}>
            <option value="">All Types</option>
            <option value="SWING">Swing</option>
            <option value="INTRADAY">Intraday</option>
          </select>
          <select style={inputStyle} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="IN_TODAY_SET">In Today's Set</option>
            <option value="RECENTLY_USED">Recently Used</option>
            <option value="AVAILABLE">Available</option>
          </select>
          <input style={inputStyle} placeholder="Scanner" value={filters.scanner} onChange={e => setFilters(f => ({ ...f, scanner: e.target.value }))} />
          <input style={inputStyle} placeholder="Symbol" value={filters.symbol} onChange={e => setFilters(f => ({ ...f, symbol: e.target.value }))} />
          <input style={{ ...inputStyle, width: 110 }} type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          <input style={{ ...inputStyle, width: 110 }} type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          <button onClick={applyFilters} className="px-3 py-1 rounded text-xs font-bold" style={{ backgroundColor: colors.accent, color: '#fff' }}>Apply</button>
          <button onClick={clearFilters} className="px-3 py-1 rounded text-xs" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>Clear</button>
          <button onClick={() => fetchScenarios()} className="px-2 py-1 rounded" style={{ backgroundColor: colors.inputBg }}>
            <RefreshCw className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </button>
        </div>

        {/* Error banner */}
        {fetchError && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: '#ef444420', border: '1px solid #ef444440', color: '#ef4444' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            {fetchError}
            <button onClick={() => fetchScenarios()} className="ml-auto underline">Retry</button>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-x-auto overflow-y-auto rounded-lg" style={{ border: `1px solid ${colors.border}` }}>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2" style={{ color: colors.secondaryText }}>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading scenarios…</span>
            </div>
          ) : !data?.items?.length ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <BookOpen className="w-8 h-8" style={{ color: colors.border }} />
              <p className="text-sm font-medium" style={{ color: colors.secondaryText }}>No scenarios found</p>
              <p className="text-xs" style={{ color: colors.secondaryText, opacity: 0.6 }}>
                {Object.values(filters).some(v => v) ? 'Try clearing filters' : 'No scenarios in the pool yet'}
              </p>
              {Object.values(filters).some(v => v) && (
                <button onClick={clearFilters} className="mt-1 px-3 py-1 rounded text-xs" style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}>Clear Filters</button>
              )}
            </div>
          ) : (
            <table className="w-full min-w-[980px] text-xs border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.inputBg }}>
                  {['ID', 'Symbol', 'Signal Date', 'Scanner', 'Type', 'RSI', 'Vol', 'Trend', 'Outcome', 'Last Served', 'Served', 'Status', 'Quality'].map(h => (
                    <th key={h} className="text-left px-2 py-2 font-semibold whitespace-nowrap" style={{ color: colors.secondaryText, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.items.map((s, i) => {
                  const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.AVAILABLE;
                  return (
                    <tr key={s.id}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: preview?.id === s.id ? `${colors.accent}15` : i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`, borderBottom: `1px solid ${colors.border}20` }}
                      onClick={() => openPreview(s.id)}>
                      <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.id}</td>
                      <td className="px-2 py-1.5 font-bold" style={{ color: colors.primaryText }}>{s.symbol}</td>
                      <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.signal_date}</td>
                      <td className="px-2 py-1.5" style={{ color: '#f59e0b' }}>{s.scanner_name?.replace(/_/g, ' ')}</td>
                      <td className="px-2 py-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: s.trade_type === 'SWING' ? '#6366f120' : '#f59e0b20', color: s.trade_type === 'SWING' ? '#818cf8' : '#f59e0b' }}>{s.trade_type}</span>
                      </td>
                      <td className="px-2 py-1.5" style={{ color: s.rsi > 70 ? '#ef4444' : s.rsi < 30 ? '#ef4444' : '#16C784' }}>{s.rsi?.toFixed(0) ?? '—'}</td>
                      <td className="px-2 py-1.5" style={{ color: s.volume_ratio >= 2 ? '#16C784' : s.volume_ratio >= 1.2 ? '#f59e0b' : '#ef4444' }}>{s.volume_ratio?.toFixed(1) ?? '—'}x</td>
                      <td className="px-2 py-1.5">{TREND_ICON(s.trend)}</td>
                      <td className="px-2 py-1.5 font-bold" style={{ color: OUTCOME_COLOR(s.outcome_pct) }}>{s.outcome_pct != null ? `${s.outcome_pct >= 0 ? '+' : ''}${s.outcome_pct.toFixed(1)}%` : '—'}</td>
                      <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.last_served_date ?? <span style={{ opacity: 0.35 }}>Never</span>}</td>
                      <td className="px-2 py-1.5 text-center" style={{ color: s.times_served > 0 ? colors.primaryText : colors.secondaryText }}>{s.times_served}</td>
                      <td className="px-2 py-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                      </td>
                      <td className="px-2 py-1.5" style={{ color: colors.secondaryText, opacity: 0.4 }}>—</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>


        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: colors.secondaryText }}>Page {data.page} of {data.total_pages} · {data.summary.total} scenarios</p>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => goPage(page - 1)} className="px-2 py-1 rounded text-xs disabled:opacity-30" style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}>
                <ChevronLeft className="w-3 h-3" />
              </button>
              <button disabled={page >= data.total_pages} onClick={() => goPage(page + 1)} className="px-2 py-1 rounded text-xs disabled:opacity-30" style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}>
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        </> /* end pool view */}
      </div>

      {/* Right: preview drawer */}
      {preview && (
        <div className="w-full xl:w-80 flex-shrink-0 flex flex-col gap-3 overflow-y-auto rounded-lg p-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold" style={{ color: colors.primaryText }}>
              {previewLoading ? 'Loading…' : previewError ? 'Error' : `${preview.symbol} · #${preview.id}`}
            </p>
            <button onClick={() => { setPreview(null); setPreviewError(null); }} style={{ color: colors.secondaryText }}><X className="w-4 h-4" /></button>
          </div>

          {previewLoading && (
            <div className="flex items-center justify-center py-8 gap-2" style={{ color: colors.secondaryText }}>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          )}

          {previewError && !previewLoading && (
            <div className="flex flex-col items-center gap-2 py-6">
              <AlertTriangle className="w-6 h-6" style={{ color: '#ef4444' }} />
              <p className="text-xs text-center" style={{ color: '#ef4444' }}>{previewError}</p>
              <button onClick={() => openPreview(preview.id)} className="text-xs underline" style={{ color: colors.secondaryText }}>Retry</button>
            </div>
          )}

          {!previewLoading && !previewError && preview.question && (
            <>
              {/* Meta */}
              <div className="space-y-1 text-[12px]" style={{ color: colors.secondaryText }}>
                <p><span className="font-semibold" style={{ color: colors.primaryText }}>{preview.signal_date}</span> · {preview.scanner_name?.replace(/_/g, ' ')}</p>
                <p>Type: <span style={{ color: preview.trade_type === 'SWING' ? '#818cf8' : '#f59e0b' }}>{preview.trade_type}</span> · Correct: <span className="font-bold" style={{ color: colors.primaryText }}>{preview.correct_answer}</span></p>
                <p>Outcome: <span style={{ color: OUTCOME_COLOR(preview.outcome_pct) }}>{preview.outcome_pct != null ? `${preview.outcome_pct >= 0 ? '+' : ''}${preview.outcome_pct.toFixed(1)}%` : '—'}</span> · {preview.exit_type}</p>
              </div>

              {/* Question */}
              <div className="rounded-lg p-2.5" style={{ backgroundColor: colors.inputBg }}>
                <p className="text-[11px] font-semibold mb-1" style={{ color: colors.secondaryText }}>QUESTION</p>
                <p className="text-[12px]" style={{ color: colors.primaryText }}>{preview.question}</p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
                {[
                  ['RSI', preview.rsi?.toFixed(1)],
                  ['Volume', preview.volume_ratio ? `${preview.volume_ratio.toFixed(1)}x` : null],
                  ['Trend', preview.trend_direction],
                  ['MACD', preview.macd_direction],
                  ['ATR%', preview.atr_pct?.toFixed(1)],
                  ['BB', preview.bb_position?.replace(/_/g, ' ')],
                ].map(([label, val]) => (
                  <div key={label} className="rounded p-1.5" style={{ backgroundColor: colors.inputBg }}>
                    <p style={{ color: colors.secondaryText }}>{label}</p>
                    <p className="font-bold" style={{ color: colors.primaryText }}>{val ?? '—'}</p>
                  </div>
                ))}
              </div>

              {/* Rotation status */}
              {preview.status && (() => {
                const sc = STATUS_CONFIG[preview.status] || STATUS_CONFIG.AVAILABLE;
                return (
                  <div className="rounded-lg p-2.5" style={{ backgroundColor: colors.inputBg }}>
                    <p className="text-[11px] font-semibold mb-1.5" style={{ color: colors.secondaryText }}>ROTATION</p>
                    <div className="flex items-center justify-between">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                      <span className="text-[11px]" style={{ color: colors.secondaryText }}>Served <span className="font-bold" style={{ color: colors.primaryText }}>{preview.times_served ?? 0}×</span></span>
                    </div>
                    <p className="text-[11px] mt-1" style={{ color: colors.secondaryText }}>
                      Last served: <span style={{ color: colors.primaryText }}>{preview.last_served_date ?? 'Never'}</span>
                    </p>
                  </div>
                );
              })()}

              {/* Usage stats */}
              {preview.usage && (
                <div className="rounded-lg p-2.5" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[11px] font-semibold mb-1.5" style={{ color: colors.secondaryText }}>USER DECISIONS</p>
                  <div className="flex flex-wrap gap-3 text-[11px]">
                    <span style={{ color: colors.secondaryText }}>Attempts: <span className="font-bold" style={{ color: colors.primaryText }}>{preview.usage.total_attempts}</span></span>
                    <span style={{ color: '#16C784' }}>E:{preview.usage.enter_count}</span>
                    <span style={{ color: '#f59e0b' }}>W:{preview.usage.wait_count}</span>
                    <span style={{ color: '#ef4444' }}>S:{preview.usage.skip_count}</span>
                  </div>
                  {preview.usage.avg_score != null && (
                    <p className="text-[11px] mt-1" style={{ color: colors.secondaryText }}>Avg score: <span className="font-bold" style={{ color: colors.primaryText }}>{preview.usage.avg_score}/100</span></p>
                  )}
                </div>
              )}

              {/* Quality scores */}
              <div className="rounded-lg p-2.5" style={{ backgroundColor: colors.inputBg }}>
                <p className="text-[11px] font-semibold mb-1.5" style={{ color: colors.secondaryText }}>QUALITY SCORES</p>
                {preview.quality_score != null ? (() => {
                  const scoreColor = v => v >= 70 ? '#16C784' : v >= 50 ? '#f59e0b' : '#ef4444';
                  const penaltyColor = v => v <= 20 ? '#16C784' : v <= 40 ? '#f59e0b' : '#ef4444';
                  return (
                    <div className="space-y-0.5">
                      <div className="flex justify-between text-[11px]">
                        <span style={{ color: colors.secondaryText }}>quality score</span>
                        <span className="font-bold" style={{ color: scoreColor(preview.quality_score) }}>{preview.quality_score}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span style={{ color: colors.secondaryText }}>clarity score</span>
                        <span style={{ color: scoreColor(preview.clarity_score) }}>{preview.clarity_score}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span style={{ color: colors.secondaryText }}>confirmation score</span>
                        <span style={{ color: scoreColor(preview.confirmation_score) }}>{preview.confirmation_score}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span style={{ color: colors.secondaryText }}>extension penalty</span>
                        <span style={{ color: penaltyColor(preview.extension_penalty) }}>{preview.extension_penalty}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span style={{ color: colors.secondaryText }}>educational value</span>
                        <span style={{ color: scoreColor(preview.educational_value_score) }}>{preview.educational_value_score}</span>
                      </div>
                    </div>
                  );
                })() : (
                  <p className="text-[11px]" style={{ color: colors.secondaryText }}>Not scored yet — run ACT Quality Scorer job</p>
                )}
              </div>

              {/* Chart */}
              {preview.pre_candles?.length > 0 && (() => {
                const allCandles = [...(preview.pre_candles || []), ...(preview.outcome_candles || [])];
                const firstTime = allCandles[0]?.time;
                const chartProductType = (typeof firstTime === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(firstTime))
                  ? 'SWING'
                  : (preview.trade_type === 'INTRADAY' ? 'INTRADAY' : 'SWING');
                return (
                  <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
                    <p className="text-[11px] px-2 pt-2 font-semibold" style={{ color: colors.secondaryText }}>CHART PREVIEW</p>
                    <TradeReplay
                      symbol={preview.symbol}
                      entryDate={preview.signal_date}
                      entryPrice={preview.pre_candles[preview.pre_candles.length - 1]?.close}
                      exitPrice={preview.outcome_candles?.[preview.outcome_candles.length - 1]?.close}
                      productType={chartProductType}
                      height={160}
                      candles={allCandles}
                      staticMode={true}
                    />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ACT Analytics Tab ────────────────────────────────────────────────────────
function ACTAnalyticsTab({ colors }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const cached = getFreshAdminCache(adminPageCache.actAnalytics, ADMIN_CACHE_TTLS_MS.actAnalytics);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }
    if (adminPageCache.actAnalytics?.value) {
      setData(adminPageCache.actAnalytics.value);
      setLoading(false);
    } else {
      setLoading(true);
    }
    (async () => {
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/admin/act/analytics`, { headers: { Authorization: `Bearer ${token}` } });
        setAdminCache('actAnalytics', res.data);
        setData(res.data);
      } catch (e) { setError(e.response?.data?.detail || 'Failed to load analytics'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="flex items-center justify-center py-16 gap-2" style={{ color: colors.secondaryText }}><RefreshCw className="w-4 h-4 animate-spin" /><span>Loading analytics…</span></div>;
  if (error)   return <div className="flex items-center gap-2 px-4 py-3 rounded-lg mt-4" style={{ backgroundColor: '#ef444420', color: '#ef4444' }}><AlertTriangle className="w-4 h-4" />{error}</div>;
  if (!data)   return null;

  const { health, distribution, by_scanner, low_quality_scenarios, served_vs_attempted } = data;

  const bucketColor = (b) => b === 'HIGH' ? '#16C784' : b === 'MEDIUM' ? '#f59e0b' : b === 'LOW' ? '#ef4444' : '#6b7280';
  const qualColor   = (q) => q >= 70 ? '#16C784' : q >= 50 ? '#f59e0b' : q >= 30 ? '#ef4444' : '#9b2c2c';

  return (
    <div className="flex flex-col gap-4 pb-6">

      {/* Pool health banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { label: 'Avg Quality', value: health.avg_quality?.toFixed(1) ?? '—', sub: 'all scenarios' },
          { label: 'Avg Swing',   value: health.avg_swing?.toFixed(1)   ?? '—', sub: 'swing pool' },
          { label: 'Avg Intraday',value: health.avg_intraday?.toFixed(1)?? '—', sub: 'intraday pool' },
        ].map(({ label, value, sub }) => (
          <div key={label} className="rounded-lg p-3 text-center" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>{label}</p>
            <p className="text-xl font-bold" style={{ color: parseFloat(value) >= 50 ? '#16C784' : parseFloat(value) >= 35 ? '#f59e0b' : '#ef4444' }}>{value}</p>
            <p className="text-[10px]" style={{ color: colors.secondaryText }}>{sub}</p>
          </div>
        ))}
      </div>

      {/* Score distribution */}
      <div className="rounded-lg p-4" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <p className="text-xs font-semibold mb-3" style={{ color: colors.secondaryText }}>SCORE DISTRIBUTION</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {['SWING', 'INTRADAY'].map(type => {
            const buckets = distribution.filter(d => d.trade_type === type);
            const total   = buckets.reduce((s, b) => s + b.count, 0);
            return (
              <div key={type}>
                <p className="text-[11px] font-semibold mb-2" style={{ color: type === 'SWING' ? '#818cf8' : '#f59e0b' }}>{type}</p>
                {['HIGH', 'MEDIUM', 'LOW', 'POOR'].map(bucket => {
                  const b   = buckets.find(d => d.bucket === bucket);
                  const cnt = b?.count || 0;
                  const pct = total > 0 ? Math.round(cnt / total * 100) : 0;
                  return (
                    <div key={bucket} className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] w-14" style={{ color: bucketColor(bucket) }}>{bucket}</span>
                      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.inputBg }}>
                        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: bucketColor(bucket) }} />
                      </div>
                      <span className="text-[11px] w-8 text-right" style={{ color: colors.secondaryText }}>{cnt}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-scanner table */}
      <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid ${colors.border}` }}>
        <p className="text-xs font-semibold px-3 py-2" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>BY SCANNER — quality + confusion rate</p>
        <table className="w-full min-w-[920px] text-xs border-collapse">
          <thead>
            <tr style={{ backgroundColor: colors.inputBg }}>
              {['Scanner', 'Type', 'N', 'Quality', 'Clarity', 'Confirm', 'Ext Pen', 'Attempts', 'E/W/S', 'Confusion%'].map(h => (
                <th key={h} className="text-left px-2 py-1.5 font-semibold" style={{ color: colors.secondaryText, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {by_scanner.map((s, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`, borderBottom: `1px solid ${colors.border}20` }}>
                <td className="px-2 py-1.5 font-medium" style={{ color: '#f59e0b' }}>{s.scanner_name?.replace(/_/g, ' ')}</td>
                <td className="px-2 py-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: s.trade_type === 'SWING' ? '#6366f120' : '#f59e0b20', color: s.trade_type === 'SWING' ? '#818cf8' : '#f59e0b' }}>{s.trade_type}</span></td>
                <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.scenario_count}</td>
                <td className="px-2 py-1.5 font-bold" style={{ color: qualColor(s.avg_quality) }}>{s.avg_quality ?? '—'}</td>
                <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.avg_clarity ?? '—'}</td>
                <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.avg_confirmation ?? '—'}</td>
                <td className="px-2 py-1.5" style={{ color: (s.avg_extension || 0) > 30 ? '#ef4444' : colors.secondaryText }}>{s.avg_extension ?? '—'}</td>
                <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.total_attempts}</td>
                <td className="px-2 py-1.5 text-[10px]" style={{ color: colors.secondaryText }}>{s.enter_count}E / {s.wait_count}W / {s.skip_count}S</td>
                <td className="px-2 py-1.5" style={{ color: s.confusion_rate > 60 ? '#f59e0b' : colors.secondaryText }}>{s.confusion_rate != null ? `${s.confusion_rate}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Low quality scenarios */}
      {low_quality_scenarios.length > 0 && (
        <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid #ef444440` }}>
          <p className="text-xs font-semibold px-3 py-2" style={{ backgroundColor: '#ef444410', color: '#ef4444' }}>POOR SCENARIOS (quality &lt; 30) — consider excluding from pool</p>
          <table className="w-full min-w-[720px] text-xs border-collapse">
            <thead>
              <tr style={{ backgroundColor: colors.inputBg }}>
                {['ID', 'Symbol', 'Scanner', 'Type', 'Quality', 'Clarity', 'Confirm', 'Ext Pen'].map(h => (
                  <th key={h} className="text-left px-2 py-1.5 font-semibold" style={{ color: colors.secondaryText, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {low_quality_scenarios.map((s, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.border}20` }}>
                  <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.id}</td>
                  <td className="px-2 py-1.5 font-bold" style={{ color: colors.primaryText }}>{s.symbol}</td>
                  <td className="px-2 py-1.5" style={{ color: '#f59e0b' }}>{s.scanner_name?.replace(/_/g, ' ')}</td>
                  <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.trade_type}</td>
                  <td className="px-2 py-1.5 font-bold" style={{ color: '#ef4444' }}>{s.quality_score ?? '—'}</td>
                  <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.clarity_score ?? '—'}</td>
                  <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.confirmation_score ?? '—'}</td>
                  <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.extension_penalty ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Served vs attempted */}
      {served_vs_attempted.length > 0 && (
        <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid ${colors.border}` }}>
          <p className="text-xs font-semibold px-3 py-2" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>MOST SERVED — rotation efficiency</p>
          <table className="w-full min-w-[720px] text-xs border-collapse">
            <thead>
              <tr style={{ backgroundColor: colors.inputBg }}>
                {['Symbol', 'Scanner', 'Type', 'Quality', 'Served', 'Attempted', 'Attempt Rate'].map(h => (
                  <th key={h} className="text-left px-2 py-1.5 font-semibold" style={{ color: colors.secondaryText, borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {served_vs_attempted.map((s, i) => {
                const rate = s.times_served > 0 ? Math.round(s.times_attempted / s.times_served * 100) : 0;
                return (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`, borderBottom: `1px solid ${colors.border}20` }}>
                    <td className="px-2 py-1.5 font-bold" style={{ color: colors.primaryText }}>{s.symbol}</td>
                    <td className="px-2 py-1.5" style={{ color: '#f59e0b' }}>{s.scanner_name?.replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.trade_type}</td>
                    <td className="px-2 py-1.5" style={{ color: qualColor(s.quality_score) }}>{s.quality_score ?? '—'}</td>
                    <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.times_served}</td>
                    <td className="px-2 py-1.5" style={{ color: colors.secondaryText }}>{s.times_attempted}</td>
                    <td className="px-2 py-1.5" style={{ color: rate < 50 ? '#ef4444' : '#16C784' }}>{rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── News Archive Tab ─────────────────────────────────────────────────────────

const NEWS_CATS = ['ALL', 'Markets', 'RBI/Policy', 'FII/DII', 'Global Cues', 'Earnings', 'Geopolitical'];

function sentimentColor(label) {
  if (!label) return '#9ca3af';
  if (label === 'positive') return '#16C784';
  if (label === 'negative') return '#EF4444';
  return '#9ca3af';
}

function formatISTAdmin(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

function NewsArchiveTab({ colors }) {
  const [summary, setSummary]           = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [category, setCategory]         = useState('ALL');
  const [articles, setArticles]         = useState([]);
  const [artLoading, setArtLoading]     = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/admin/market-news/summary`)
      .then(r => setSummary(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setCategory('ALL');
    setArticles([]);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    setArtLoading(true);
    axios.get(`${API_URL}/admin/market-news`, { params: { news_date: selectedDate, category } })
      .then(r => setArticles(r.data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setArtLoading(false));
  }, [selectedDate, category]);

  function statusStyle(status) {
    if (status === 'complete') return { color: '#16C784', bg: '#16C78420' };
    if (status === 'partial')  return { color: '#f59e0b', bg: '#f59e0b20' };
    return { color: '#9ca3af', bg: '#9ca3af20' };
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Newspaper className="w-4 h-4" style={{ color: colors.accent }} />
        <h2 className="text-sm font-semibold" style={{ color: colors.primaryText }}>News Archive</h2>
        <span className="text-xs" style={{ color: colors.secondaryText }}>Pre-market window per sim_date</span>
      </div>

      {/* Summary grid */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: colors.border }}>
        <div className="px-3 py-2 text-xs font-medium" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
          Last 60 days — click a date to view articles
        </div>
        {summaryLoading ? (
          <div className="p-4 text-xs text-center" style={{ color: colors.secondaryText }}>Loading summary…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  {['Date', 'Articles', 'Status'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: colors.secondaryText }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((row, i) => {
                  const s = statusStyle(row.status);
                  const isSelected = selectedDate === row.date;
                  return (
                    <tr
                      key={row.date}
                      onClick={() => setSelectedDate(row.date)}
                      className="cursor-pointer transition-colors"
                      style={{
                        backgroundColor: isSelected ? `${colors.accent}15` : i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`,
                        borderBottom: `1px solid ${colors.border}20`,
                        outline: isSelected ? `1px solid ${colors.accent}40` : 'none',
                      }}
                    >
                      <td className="px-3 py-1.5 font-medium" style={{ color: colors.primaryText }}>{row.date}</td>
                      <td className="px-3 py-1.5" style={{ color: colors.secondaryText }}>{row.total}</td>
                      <td className="px-3 py-1.5">
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {summary.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-4 text-center" style={{ color: colors.secondaryText }}>No data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Article list */}
      {selectedDate && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium" style={{ color: colors.primaryText }}>
              {selectedDate} — pre-market news
            </span>
            <span className="text-xs" style={{ color: colors.secondaryText }}>{articles.length} articles</span>
          </div>

          {/* Category pills */}
          <div className="flex gap-1.5 flex-wrap">
            {NEWS_CATS.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
                style={{
                  backgroundColor: category === c ? colors.accent : `${colors.accent}15`,
                  color: category === c ? '#fff' : colors.accent,
                }}
              >
                {c}
              </button>
            ))}
          </div>

          {artLoading ? (
            <div className="text-xs text-center py-4" style={{ color: colors.secondaryText }}>Loading…</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {articles.map((art, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 p-2.5 rounded-lg"
                  style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}20` }}
                >
                  <span
                    className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: sentimentColor(art.sentiment_label) }}
                  />
                  <div className="flex-1 min-w-0">
                    <a
                      href={art.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium leading-snug hover:underline"
                      style={{ color: colors.primaryText }}
                    >
                      {art.title}
                      <ExternalLink className="inline ml-1 w-3 h-3 opacity-50" />
                    </a>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs" style={{ color: colors.secondaryText }}>{art.source}</span>
                      <span className="text-xs" style={{ color: colors.secondaryText }}>{formatISTAdmin(art.published_at)}</span>
                      {art.category && (
                        <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
                          {art.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {articles.length === 0 && (
                <div className="text-xs text-center py-4" style={{ color: colors.secondaryText }}>No articles for this date/category</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── F&O Data Health Tab ───────────────────────────────────────────────────────

function FoDataHealthTab({ colors }) {
  const [typeFilter, setTypeFilter]         = useState('futures');
  const [underlying, setUnderlying]         = useState('NIFTY50');
  const [summary, setSummary]               = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedDate, setSelectedDate]     = useState(null);
  const [contracts, setContracts]           = useState(null);
  const [contractsLoading, setContractsLoading] = useState(false);

  useEffect(() => {
    const cached = getFreshAdminCache(adminPageCache.foHealthSummary, ADMIN_CACHE_TTLS_MS.foHealthSummary);
    if (cached) {
      setSummary(Array.isArray(cached) ? cached : []);
      setSummaryLoading(false);
      return;
    }
    if (adminPageCache.foHealthSummary?.value) {
      setSummary(Array.isArray(adminPageCache.foHealthSummary.value) ? adminPageCache.foHealthSummary.value : []);
      setSummaryLoading(false);
    } else {
      setSummaryLoading(true);
    }
    axios.get(`${API_URL}/admin/fo-health/summary`)
      .then(r => {
        setAdminCache('foHealthSummary', Array.isArray(r.data) ? r.data : []);
        setSummary(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => {
    setSelectedDate(null);
    setContracts(null);
  }, [underlying]);

  useEffect(() => {
    if (!selectedDate) return;
    const cacheKey = `${selectedDate}:${underlying}`;
    const cached = getFreshAdminCache(adminPageCache.foHealthContracts.get(cacheKey), ADMIN_CACHE_TTLS_MS.foHealthContracts);
    if (cached) {
      setContracts(cached);
      setContractsLoading(false);
      return;
    }
    setContractsLoading(true);
    setContracts(adminPageCache.foHealthContracts.get(cacheKey)?.value || null);
    axios.get(`${API_URL}/admin/fo-health/contracts`, {
      params: { check_date: selectedDate, underlying },
    })
      .then(r => {
        adminPageCache.foHealthContracts.set(cacheKey, { value: r.data, at: Date.now() });
        setContracts(r.data);
      })
      .catch(() => setContracts(null))
      .finally(() => setContractsLoading(false));
  }, [selectedDate, underlying]);

  const [expiryFilter, setExpiryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortCol, setSortCol]           = useState('expiry');
  const [sortAsc, setSortAsc]           = useState(true);

  useEffect(() => {
    setExpiryFilter('ALL'); setStatusFilter('ALL');
    setSortCol('expiry'); setSortAsc(true);
  }, [selectedDate, typeFilter]);

  const statusColor = s => s === 'ok' ? '#16C784' : s === 'warn' ? '#f59e0b' : s === 'fail' ? '#ef4444' : '#9ca3af';
  const statusBg    = s => s === 'ok' ? '#16C78420' : s === 'warn' ? '#f59e0b20' : s === 'fail' ? '#ef444420' : '#9ca3af20';
  const dateStatus  = row => underlying === 'NIFTY50' ? row.nifty50_status : row.banknifty_status;

  const typeInstruments = typeFilter === 'futures' ? ['FUT'] : ['CE', 'PE'];

  const availableExpiries = [...new Set(
    (contracts?.contracts || [])
      .filter(c => typeInstruments.includes(c.instrument_type))
      .map(c => c.expiry)
  )].sort();

  const filteredContracts = (contracts?.contracts || [])
    .filter(c => typeInstruments.includes(c.instrument_type))
    .filter(c => expiryFilter === 'ALL' || c.expiry === expiryFilter)
    .filter(c => {
      if (statusFilter === 'ALL')     return true;
      if (statusFilter === 'ok')      return c.status === 'ok';
      if (statusFilter === 'missing') return c.candle_count === 0;
      if (statusFilter === 'partial') return c.candle_count > 0 && c.status !== 'ok';
      return true;
    })
    .sort((a, b) => {
      let va, vb;
      if      (sortCol === 'strike')  { va = a.strike_price ?? 0;  vb = b.strike_price ?? 0; }
      else if (sortCol === 'candles') { va = a.candle_count;        vb = b.candle_count; }
      else if (sortCol === 'symbol')  { va = a.symbol;              vb = b.symbol; }
      else if (sortCol === 'status')  { va = a.status;              vb = b.status; }
      else                            { va = a.expiry;              vb = b.expiry; }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return (a.strike_price ?? 0) - (b.strike_price ?? 0);
    });

  function toggleSort(col) {
    if (sortCol === col) setSortAsc(v => !v);
    else { setSortCol(col); setSortAsc(true); }
  }
  function SortArrow({ col }) {
    if (sortCol !== col) return <span style={{ opacity: 0.25, marginLeft: 3 }}>↕</span>;
    return <span style={{ color: colors.accent, marginLeft: 3 }}>{sortAsc ? '↑' : '↓'}</span>;
  }

  const fmtPrice = n => n != null
    ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

  return (
    <div className="flex flex-col gap-3">

      {/* Header + Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" style={{ color: colors.accent }} />
          <h2 className="text-sm font-semibold" style={{ color: colors.primaryText }}>F&amp;O Data Health</h2>
          <span className="text-xs" style={{ color: colors.secondaryText }}>Last 45 days — candle &amp; expiry coverage</span>
        </div>

        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: colors.secondaryText }}>Type</span>
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
              {[['futures', 'Futures'], ['options', 'Options']].map(([k, l]) => (
                <button key={k} onClick={() => setTypeFilter(k)}
                  className="px-3 py-1 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: typeFilter === k ? colors.accent : 'transparent',
                    color: typeFilter === k ? '#fff' : colors.secondaryText,
                  }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: colors.secondaryText }}>Underlying</span>
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
              {['NIFTY50', 'BANKNIFTY'].map(u => (
                <button key={u} onClick={() => setUnderlying(u)}
                  className="px-3 py-1 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: underlying === u ? colors.accent : 'transparent',
                    color: underlying === u ? '#fff' : colors.secondaryText,
                  }}>
                  {u}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-col lg:flex-row gap-3" style={{ minHeight: '520px' }}>

        {/* Left: date list */}
        <div className="flex-shrink-0 flex flex-col rounded-lg border overflow-hidden w-full lg:w-[175px]"
          style={{ borderColor: colors.border }}>
          <div className="px-3 py-2 text-xs font-medium flex-shrink-0"
            style={{ backgroundColor: colors.inputBg, color: colors.secondaryText, borderBottom: `1px solid ${colors.border}` }}>
            Select date
          </div>
          <div className="overflow-y-auto flex-1 max-h-48 lg:max-h-none">
            {summaryLoading ? (
              <div className="p-3 text-xs text-center" style={{ color: colors.secondaryText }}>Loading…</div>
            ) : summary.length === 0 ? (
              <div className="p-3 text-xs text-center" style={{ color: colors.secondaryText }}>No data yet</div>
            ) : summary.map((row, i) => {
              const st = dateStatus(row);
              const isSel = selectedDate === row.date;
              return (
                <button key={row.date} onClick={() => setSelectedDate(row.date)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs transition-colors text-left"
                  style={{
                    backgroundColor: isSel ? `${colors.accent}18` : i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`,
                    borderLeft: `3px solid ${isSel ? colors.accent : 'transparent'}`,
                    borderBottom: `1px solid ${colors.border}15`,
                  }}>
                  <span style={{ color: isSel ? colors.primaryText : colors.secondaryText, fontWeight: isSel ? 600 : 400 }}>
                    {row.date}
                  </span>
                  <span className="w-2 h-2 rounded-full flex-shrink-0 ml-1"
                    style={{ backgroundColor: statusColor(st) }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: detail */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          {!selectedDate ? (
            <div className="flex-1 flex items-center justify-center rounded-lg border"
              style={{ borderColor: colors.border }}>
              <span className="text-xs" style={{ color: colors.secondaryText }}>
                ← Select a date to view contracts and candle data
              </span>
            </div>
          ) : (
            <>
              {/* Sim price + meta strip */}
              <div className="rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 flex-wrap"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
                <div>
                  <div className="text-xs mb-0.5" style={{ color: colors.secondaryText }}>Date</div>
                  <div className="text-sm font-semibold" style={{ color: colors.primaryText }}>{selectedDate}</div>
                </div>
                <div>
                  <div className="text-xs mb-0.5" style={{ color: colors.secondaryText }}>Underlying</div>
                  <div className="text-sm font-semibold" style={{ color: colors.primaryText }}>{underlying}</div>
                </div>
                <div>
                  <div className="text-xs mb-0.5" style={{ color: colors.secondaryText }}>Sim Close (EOD)</div>
                  <div className="text-sm font-semibold" style={{ color: '#16C784' }}>
                    {contracts ? fmtPrice(contracts.spot_price) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-0.5" style={{ color: colors.secondaryText }}>Viewing</div>
                  <div className="text-sm font-semibold" style={{ color: colors.accent }}>
                    {typeFilter === 'futures' ? 'Futures' : 'Options (CE + PE)'}
                  </div>
                </div>
                {contracts && (
                  <div className="sm:ml-auto flex flex-wrap gap-4 text-xs items-center">
                    <span style={{ color: colors.secondaryText }}>
                      {filteredContracts.length}
                      {filteredContracts.length !== (contracts?.contracts || []).filter(c => typeInstruments.includes(c.instrument_type)).length
                        ? ` of ${(contracts?.contracts || []).filter(c => typeInstruments.includes(c.instrument_type)).length}`
                        : ''} contracts
                    </span>
                    {[['ok', '#16C784', 'complete'], ['warn', '#f59e0b', 'sparse'], ['fail', '#ef4444', 'missing']].map(([s, c, l]) => {
                      const cnt = filteredContracts.filter(x => x.status === s).length;
                      return cnt > 0 ? (
                        <span key={s} style={{ color: c }}>
                          <span className="font-semibold">{cnt}</span> {l}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Contracts filter bar */}
              {contracts && !contractsLoading && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
                  {/* Expiry pills */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium flex-shrink-0" style={{ color: colors.secondaryText }}>Expiry</span>
                    <div className="flex gap-1 flex-wrap">
                      {['ALL', ...availableExpiries].map(e => (
                        <button key={e} onClick={() => setExpiryFilter(e)}
                          className="px-2 py-0.5 rounded text-xs font-medium transition-colors whitespace-nowrap"
                          style={{
                            backgroundColor: expiryFilter === e ? colors.accent : `${colors.accent}15`,
                            color: expiryFilter === e ? '#fff' : colors.accent,
                          }}>
                          {e === 'ALL' ? 'All expiries' : e}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Status filter */}
                  <div className="flex items-center gap-1.5 sm:ml-auto flex-shrink-0">
                    <span className="text-xs font-medium" style={{ color: colors.secondaryText }}>Status</span>
                    <div className="flex gap-1">
                      {[
                        ['ALL',     colors.secondaryText, 'All'],
                        ['ok',      '#16C784',            'OK'],
                        ['partial', '#f59e0b',            'Sparse'],
                        ['missing', '#ef4444',            'Missing'],
                      ].map(([k, c, l]) => (
                        <button key={k} onClick={() => setStatusFilter(k)}
                          className="px-2 py-0.5 rounded text-xs font-medium transition-colors"
                          style={{
                            backgroundColor: statusFilter === k ? c : `${c}18`,
                            color: statusFilter === k ? '#fff' : c,
                          }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Contracts table */}
              {contractsLoading ? (
                <div className="flex-1 flex items-center justify-center text-xs"
                  style={{ color: colors.secondaryText }}>Loading contracts…</div>
              ) : contracts ? (
                <div className="rounded-lg border overflow-hidden" style={{ borderColor: colors.border }}>
                  <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0" style={{ backgroundColor: colors.cardBg, zIndex: 1 }}>
                        <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                          {/* Sortable headers */}
                          {[
                            { label: 'Symbol',  col: 'symbol' },
                            { label: 'Expiry',  col: 'expiry' },
                            ...(typeFilter === 'options' ? [{ label: 'CE/PE', col: null }, { label: 'Strike', col: 'strike' }] : []),
                            { label: 'Candles', col: 'candles' },
                            { label: 'First',   col: null },
                            { label: 'Last',    col: null },
                            { label: 'Status',  col: 'status' },
                          ].map(({ label, col }) => (
                            <th key={label}
                              className={`px-3 py-2 text-left font-medium whitespace-nowrap${col ? ' cursor-pointer select-none' : ''}`}
                              style={{ color: sortCol === col ? colors.primaryText : colors.secondaryText }}
                              onClick={col ? () => toggleSort(col) : undefined}>
                              {label}{col && <SortArrow col={col} />}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContracts.map((c, i) => (
                          <tr key={c.symbol}
                            style={{
                              backgroundColor: i % 2 === 0 ? 'transparent' : `${colors.inputBg}50`,
                              borderBottom: `1px solid ${colors.border}15`,
                              opacity: c.candle_count === 0 ? 0.5 : 1,
                            }}>
                            <td className="px-3 py-1.5 font-mono font-medium" style={{ color: colors.primaryText }}>
                              {c.symbol}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap" style={{ color: colors.secondaryText }}>
                              {c.expiry}
                            </td>
                            {typeFilter === 'options' && (
                              <>
                                <td className="px-3 py-1.5">
                                  <span className="px-1.5 py-0.5 rounded text-xs font-medium"
                                    style={{
                                      backgroundColor: c.instrument_type === 'CE' ? '#16C78418' : '#ef444418',
                                      color: c.instrument_type === 'CE' ? '#16C784' : '#ef4444',
                                    }}>
                                    {c.instrument_type}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 font-medium" style={{ color: colors.primaryText }}>
                                  {c.strike_price != null ? c.strike_price.toLocaleString('en-IN') : '—'}
                                </td>
                              </>
                            )}
                            <td className="px-3 py-1.5 font-semibold" style={{ color: statusColor(c.status) }}>
                              {c.candle_count.toLocaleString()}
                            </td>
                            <td className="px-3 py-1.5 font-mono" style={{ color: colors.secondaryText }}>
                              {c.first_candle || '—'}
                            </td>
                            <td className="px-3 py-1.5 font-mono" style={{ color: colors.secondaryText }}>
                              {c.last_candle || '—'}
                            </td>
                            <td className="px-3 py-1.5">
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: statusBg(c.status), color: statusColor(c.status) }}>
                                {c.candle_count === 0 ? 'no data' : c.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {filteredContracts.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-3 py-8 text-center"
                              style={{ color: colors.secondaryText }}>
                              No {typeFilter} contracts for {underlying} on {selectedDate}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs"
                  style={{ color: colors.secondaryText }}>Failed to load contract data</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const VALID_ADMIN_TABS = ['users', 'database', 'job-dashboard', 'ai-logs', 'banner', 'act-scenarios', 'act-analytics', 'news-archive', 'fo-health'];

export default function AdminPage() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const tab = VALID_ADMIN_TABS.includes(rawTab) ? rawTab : 'users';

  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const tabs = [
    { key: 'users',          label: 'Users',         Icon: Users },
    { key: 'database',       label: 'Database',      Icon: Database },
    { key: 'job-dashboard',  label: 'Jobs',          Icon: Activity },
    { key: 'ai-logs',        label: 'AI Logs',       Icon: Brain },
    { key: 'banner',         label: 'Banner',        Icon: Megaphone },
    { key: 'act-scenarios',  label: 'ACT Scenarios', Icon: BookOpen },
    { key: 'act-analytics', label: 'ACT Analytics', Icon: Activity },
    { key: 'news-archive',  label: 'News Archive',  Icon: Newspaper },
    { key: 'fo-health',     label: 'F&O Health',    Icon: Activity },
  ];

  return (
    <div className="flex flex-col gap-4" style={{ minHeight: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.accent}20` }}>
            <Shield className="w-4 h-4" style={{ color: colors.accent }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: colors.primaryText }}>Admin Panel</h1>
            <p className="text-xs" style={{ color: colors.secondaryText }}>Signed in as {user?.email}</p>
          </div>
        </div>

        {/* Tab bar — full width row on mobile so all tabs fit */}
        <div className="flex gap-1 p-1 rounded-lg overflow-x-auto" style={{ backgroundColor: colors.inputBg }}>
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: tab === key ? colors.cardBg : 'transparent',
                color: tab === key ? colors.primaryText : colors.secondaryText,
                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'users'          && <UsersTab          colors={colors} />}
        {tab === 'database'       && <DatabaseTab       colors={colors} />}
        {tab === 'job-dashboard'  && <JobDashboardTab   colors={colors} />}
        {tab === 'ai-logs'        && <LogAnalysisTab    colors={colors} />}
        {tab === 'banner'         && <BannerTab         colors={colors} />}
        {tab === 'act-scenarios'  && <ACTScenariosTab   colors={colors} />}
        {tab === 'act-analytics'  && <ACTAnalyticsTab   colors={colors} />}
        {tab === 'news-archive'   && <NewsArchiveTab    colors={colors} />}
        {tab === 'fo-health'      && <FoDataHealthTab  colors={colors} />}
      </div>
    </div>
  );
}
